import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Loader2, FileText, Users, Bed, Plus, Trash2, DollarSign, Calculator, Receipt, Hotel, Car, MapPin, Check, X, AlertCircle, Settings, CreditCard, Ship, ArrowRight, Info, AlertTriangle, Calendar } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { CustomVehicleTable } from './CustomVehicleTable';
import { MiscSummaryTable } from '../QuotationView/MiscSummaryTable';
import { PackageTotalPanel, MiscLines } from './PackageTotalPanel';
import { transformCategoriesForPricing, vehicleUplift, applyMarkupDistribution } from '../../utils/quotationUtils';
import TermsSection from './TermsSection';
import TripSummaryAccordion from './TripSummaryAccordion';
import { ClearableNumberInput } from '../ui/clearable-number-input';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { MealPlanPricingTable, SketchItineraryTable } from '../QuotationDisplay';
// NS-1 File 3: unified preview — wired type-by-type (Predefined first)
import QuotationPreview from '../QuotationPreview';
import { normalizePreviewData } from '../../utils/previewNormalizer';

const ROOM_CATEGORIES = [
  'Budget', 'Standard', 'Deluxe', 'Superior', 'Premium',
  'Super Premium', 'Luxury', 'Super Luxury', 'Ultra Luxury'
];

const MEAL_PLANS = [
  { value: 'EP', label: 'Room Only' },
  { value: 'CP', label: 'Breakfast Only' },
  { value: 'MAP', label: 'Breakfast + Dinner' },
  { value: 'AP', label: 'All Meals' },
];

const COST_TYPES = [
  { value: 'per_person', label: 'Per Person' },
  { value: 'per_room', label: 'Per Room' },
  { value: 'per_night', label: 'Per Night' },
  { value: 'total', label: 'Total (Fixed)' },
];

// Flag colors and icons for hotel resolution status
const getFlagStyle = (flag) => {
  switch(flag) {
    case 'match':
      return { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-700', icon: '✓', label: 'Exact Match' };
    case 'upgrade-near':
      return { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', icon: '↑', label: 'Upgraded (+1)' };
    case 'upgrade-far':
      return { bg: 'bg-amber-100', border: 'border-amber-500', text: 'text-amber-800', icon: '↑↑', label: 'Upgraded (+2)' };
    case 'downgrade-near':
      return { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', icon: '↓', label: 'Downgraded (-1)' };
    case 'downgrade-far':
      return { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-800', icon: '↓↓', label: 'Downgraded (-2)' };
    case 'unresolved':
      return { bg: 'bg-red-100', border: 'border-red-500', text: 'text-red-700', icon: '✗', label: 'Not Available' };
    case 'user-selected':
    case 'override':
      return { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-600', icon: '●', label: 'User Selected' };
    default:
      return { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-600', icon: '?', label: 'Unknown' };
  }
};

const PreferencesStep = ({
  formData,
  handleInputChange,
  toggleMealPlan,
  toggleCategory,
  availableCategories,
  availableMealPlans,
  calculating,
  calculationResult,
  loading,
  onCalculate,
  classificationMode,
  switchClassificationMode,
  onSave,  // Changed from onProceedToPreview - now saves directly
  vehicleOptions = [],
  selectedVehicles = [],
  onVehicleSelectionChange,
  routes = [], // Add routes prop for P2P matching
  hidePreview = true, // Hide preview sections (now shown in Step 3)
  showOnlyPreview = false, // Show only preview sections (for Step 3)
  hasChanges = false, // For recalculate warning
  setHasChanges, // To mark changes
  staleChanges = [], // [{key,label,text}] — what changed since the last calculation
  onRecalculateAndSave, // Recalculate then save in one tap
  onBack, // Back button handler
}) => {
  const { isB2BAgent, hideVehicleCosts } = useAuth();
  // Check if this is a manual package
  const isManualPackage = formData.route_type === 'manual_package';
  // Check if this is a DMC package
  const isDMCPackage = formData.route_type === 'dmc_package';
  // B2B agents never set their own margin on Predefined/Custom — Admin's
  // default margin is applied automatically (server-side) and hidden.
  const hideMarkupForB2B = isB2BAgent && ['predefined', 'predefined_route', 'custom', 'custom_package'].includes(formData.route_type);
  // Markup-inclusive vehicle total for lists without total_quoted/uplift
  // (e.g. raw transfer rates). B2B forms carry no markup → raw.
  const vehicleMarkupFactor = (formData.markup_type || 'percent') === 'percent'
    ? 1 + (Number(formData.markup_pct) || 0) / 100
    : 1;
  const rawVehicleTotal = (days) => (x) =>
    (x.rate_per_day || x.rate_amount || x.rate || 0) * days * (x.vehicle_count || x.count || 1) * vehicleMarkupFactor;
  // Predefined + Custom package only (per user request 2026-10-14): same
  // fallback as rawVehicleTotal, but applies each vehicle's own GST slab
  // (from Transfer Rate Master) before markup — matching the backend's
  // post-Calculate formula (vehicle_cost = base*(1+gst%) , uplift = diff*(1+markup%)).
  // Manual/DMC keep using the plain rawVehicleTotal above, untouched.
  const rawVehicleTotalWithGst = (days) => (x) => {
    const base = (x.rate_per_day || x.rate_amount || x.rate || 0) * days * (x.vehicle_count || x.count || 1);
    const gstPct = Number(x.gst_slab ?? x.gst_pct ?? 0);
    return base * (1 + gstPct / 100) * vehicleMarkupFactor;
  };
  
  // Manual Package cost inputs - now supports per meal plan
  const [manualCosts, setManualCosts] = useState({
    // { [mealPlan]: { [placeName]: { [category]: { hotel_name, room_type, cost } } } }
    mealPlanData: {},
    selectedVehicles: selectedVehicles || [], // Initialize from prop (for clone scenarios)
    vehicleCosts: {}, // { [vehicleType]: { total: number, uplift: number } }
    total_override: '',
  });
  
  // Sync selectedVehicles from prop when cloning
  useEffect(() => {
    if (selectedVehicles && selectedVehicles.length > 0) {
      setManualCosts(prev => {
        // Only update if different to avoid infinite loops
        if (JSON.stringify(prev.selectedVehicles) !== JSON.stringify(selectedVehicles)) {
          return { ...prev, selectedVehicles };
        }
        return prev;
      });
    }
  }, [selectedVehicles]);
  
  // Point-to-Point Transfers State with Smart Fallback Detection
  const [p2pTransfers, setP2pTransfers] = useState({
    available: {},  // { "Kochi->Munnar": [...transfers] }
    selected: {},   // { "p2p_id": { selected: true, quantity: 1 } }
    loading: false,
    // Smart Fallback fields
    detectedMode: 'DAILY',  // DAILY | LEG_BASED | HYBRID
    modeLabel: 'Daily Rate',
    hasRouteBasedRates: false,
    hasDailyRates: false,
    hasP2pCoverage: false,
    p2pCoveragePercent: 0,
    recommendedAction: '',
    missingLegs: [],
    coveredLegs: [],
  });
  
  // Ticket #285275: Point-to-Point transfers disabled (set false to restore)
  const P2P_DISABLED = true;
  // Fetch Point-to-Point transfers with Smart Fallback mode detection
  useEffect(() => {
    const fetchP2PTransfers = async () => {
      if (P2P_DISABLED) {
        setP2pTransfers(prev => ({ ...prev, available: {}, selected: {}, loading: false }));
        return;
      }
      // Get route places from formData
      const places = [];
      
      if (formData.route_type === 'predefined_route' || formData.route_type === 'predefined') {
        // Predefined route: find route from routes array and extract places
        const selectedRoute = routes.find(r => r.route_id === formData.route_id);
        if (selectedRoute?.stops) {
          selectedRoute.stops.forEach(stop => {
            if (stop.place_name) places.push(stop.place_name);
          });
        }
      } else if (formData.custom_route?.length > 0) {
        // Custom route: extract places from custom_route
        formData.custom_route.forEach(stop => {
          const placeName = stop.place_name || stop.place;
          if (placeName) places.push(placeName);
        });
      }
      
      if (places.length < 2) {
        setP2pTransfers(prev => ({ ...prev, available: {}, loading: false }));
        return;
      }
      
      try {
        setP2pTransfers(prev => ({ ...prev, loading: true }));
        const routeStr = places.join(',');
        
        // Include tour_date for date-filtered P2P matching
        const tourDate = formData.arrival_date 
          ? new Date(formData.arrival_date).toISOString().split('T')[0] 
          : new Date().toISOString().split('T')[0];
        
        const response = await api.get(
          `/point-to-point/route-matches?route=${encodeURIComponent(routeStr)}&tour_date=${tourDate}`
        );
        
        const data = response.data;
        
        // Get transfers from p2p_data (new format) or directly (old format)
        const transfers = data?.p2p_data?.transfers || data?.transfers || {};
        const coverage = data?.p2p_data?.coverage || data?.coverage || {};
        
        // Smart auto-selection: Select CHEAPEST Mandatory transfer per leg
        // ONLY if there are Mandatory transfers - DO NOT auto-select Optional transfers
        const autoSelected = {};
        Object.entries(transfers).forEach(([legKey, legTransfers]) => {
          if (!Array.isArray(legTransfers) || legTransfers.length === 0) return;
          
          // Find ONLY Mandatory transfers for this leg
          const mandatoryTransfers = legTransfers.filter(
            t => t.inclusion_type === 'Mandatory' || t.show_as === 'Mandatory'
          );
          
          // Only auto-select if there are Mandatory transfers
          // If all transfers are Optional, do NOT auto-select any
          if (mandatoryTransfers.length > 0) {
            // Sort by rate and select the cheapest one
            const sorted = [...mandatoryTransfers].sort((a, b) => 
              (a.base_rate || a.rate || 0) - (b.base_rate || b.rate || 0)
            );
            const cheapest = sorted[0];
            autoSelected[cheapest.p2p_id] = { selected: true, quantity: 1, isBase: true };
          }
          // If only Optional transfers exist, don't auto-select anything
        });
        
        setP2pTransfers(prev => ({
          ...prev,
          available: transfers,
          selected: { ...prev.selected, ...autoSelected },
          loading: false,
          // Smart Fallback fields from API response
          detectedMode: data?.mode || 'DAILY',
          modeLabel: data?.mode_label || 'Daily Rate',
          hasRouteBasedRates: data?.has_route_based || false,
          hasDailyRates: data?.has_daily_rates || false,
          hasP2pCoverage: data?.has_p2p_coverage || false,
          p2pCoveragePercent: data?.p2p_coverage_percent || coverage?.coverage_percent || 0,
          recommendedAction: data?.recommended_action || '',
          missingLegs: coverage?.missing_legs || [],
          coveredLegs: coverage?.covered_legs || [],
        }));
      } catch (error) {
        console.error('Failed to fetch P2P transfers:', error);
        setP2pTransfers(prev => ({ ...prev, loading: false }));
      }
    };
    
    fetchP2PTransfers();
  }, [formData.route_type, formData.route_id, formData.custom_route?.length, formData.arrival_date, routes]);
  
  // Toggle P2P transfer selection (allow both mandatory and optional to be toggled)
  // Toggle P2P transfer selection - with "upgrade replaces base" logic
  const toggleP2PTransfer = (p2pId, legKey) => {
    setP2pTransfers(prev => {
      const newSelected = { ...prev.selected };
      const currentlySelected = newSelected[p2pId]?.selected;
      
      // If selecting this transfer
      if (!currentlySelected) {
        // Deselect all other transfers for this leg (upgrade replaces base)
        const legTransfers = prev.available[legKey] || [];
        legTransfers.forEach(t => {
          if (t.p2p_id !== p2pId && newSelected[t.p2p_id]?.selected) {
            newSelected[t.p2p_id] = { ...newSelected[t.p2p_id], selected: false };
          }
        });
        newSelected[p2pId] = { selected: true, quantity: newSelected[p2pId]?.quantity || 1 };
      } else {
        // If deselecting, check if this is the only selected transfer for the leg
        // and if there are mandatory transfers, auto-select the cheapest
        newSelected[p2pId] = { ...newSelected[p2pId], selected: false };
        
        // Check if any transfer is still selected for this leg
        const legTransfers = prev.available[legKey] || [];
        const anySelected = legTransfers.some(t => 
          t.p2p_id !== p2pId && newSelected[t.p2p_id]?.selected
        );
        
        // If nothing selected, auto-select cheapest Mandatory (ONLY if Mandatory exists)
        if (!anySelected) {
          const mandatoryTransfers = legTransfers.filter(
            t => t.inclusion_type === 'Mandatory' || t.show_as === 'Mandatory'
          );
          // Only auto-select if there are Mandatory transfers
          // If all are Optional, leave none selected
          if (mandatoryTransfers.length > 0) {
            const sorted = [...mandatoryTransfers].sort((a, b) => 
              (a.base_rate || a.rate || 0) - (b.base_rate || b.rate || 0)
            );
            const cheapest = sorted[0];
            newSelected[cheapest.p2p_id] = { selected: true, quantity: 1, isBase: true };
          }
        }
      }
      
      return { ...prev, selected: newSelected };
    });
  };
  
  // Update P2P transfer quantity
  const updateP2PQuantity = (p2pId, quantity) => {
    setP2pTransfers(prev => ({
      ...prev,
      selected: {
        ...prev.selected,
        [p2pId]: {
          ...prev.selected[p2pId],
          quantity: Math.max(1, quantity),
        },
      },
    }));
  };
  
  // Calculate total P2P cost - only counts selected transfer per leg
  const calculateP2PCost = () => {
    let total = 0;
    Object.entries(p2pTransfers.available).forEach(([leg, transfers]) => {
      if (!Array.isArray(transfers)) return;
      
      transfers.forEach(t => {
        const selection = p2pTransfers.selected[t.p2p_id];
        const isSelected = selection?.selected === true;
        
        if (isSelected) {
          const qty = selection?.quantity || 1;
          const rate = t.base_rate || t.rate || 0;
          const pax = formData.no_adults + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0);
          
          if (t.pricing_type === 'PER_PERSON') {
            total += rate * pax * qty;
          } else {
            total += rate * qty;
          }
        }
      });
    });
    return total;
  };
  
  // Get the base (cheapest mandatory) transfer for a leg
  const getBaseTranfer = (legKey) => {
    const legTransfers = p2pTransfers.available[legKey] || [];
    const mandatoryTransfers = legTransfers.filter(
      t => t.inclusion_type === 'Mandatory' || t.show_as === 'Mandatory'
    );
    if (mandatoryTransfers.length === 0) return null;
    
    const sorted = [...mandatoryTransfers].sort((a, b) => 
      (a.base_rate || a.rate || 0) - (b.base_rate || b.rate || 0)
    );
    return sorted[0];
  };
  
  const roomCounts = {
    single: formData.room_config?.single_rooms || calculationResult?.single_rooms || 0,
    double: formData.room_config?.double_rooms || calculationResult?.double_rooms || 0,
    triple: formData.room_config?.triple_rooms || calculationResult?.triple_rooms || 0,
  };
  const quoteRoomsLabel = [['single', 'Single'], ['double', 'Double'], ['triple', 'Triple']]
    .filter(([k]) => roomCounts[k] > 0).map(([k, l]) => `${roomCounts[k]} ${l}`).join(' · ') || '—';
  const hotelRoomsText = (n) => {
    const c = Number(n) || 0;
    const total = roomCounts.single + roomCounts.double + roomCounts.triple;
    return c > 0 && c !== total ? `${c} Room${c > 1 ? 's' : ''}` : quoteRoomsLabel;
  };

  // Calculate uplift amount compared to base
  const getUpliftAmount = (transfer, legKey) => {
    const base = getBaseTranfer(legKey);
    if (!base) return 0;
    const baseRate = base.base_rate || base.rate || 0;
    const thisRate = transfer.base_rate || transfer.rate || 0;
    return thisRate - baseRate;
  };
  
  // Sync P2P selections to formData when they change
  useEffect(() => {
    if (Object.keys(p2pTransfers.available).length > 0) {
      // Build the selected P2P transfers array for backend
      const selectedP2PTransfers = [];
      Object.entries(p2pTransfers.available).forEach(([leg, transfers]) => {
        transfers.forEach(t => {
          const selection = p2pTransfers.selected[t.p2p_id];
          const isMandatory = t.inclusion_type === 'Mandatory' || t.show_as === 'Mandatory';
          const isSelected = selection?.selected === true;
          
          if (isSelected) {
            selectedP2PTransfers.push({
              p2p_id: t.p2p_id,
              from_place: t.from_place,
              to_place: t.to_place,
              transfer_type: t.transfer_type,
              description: t.description,
              vehicle_type: t.vehicle_type,
              pricing_type: t.pricing_type,
              variant_name: t.variant_name,
              max_pax: t.max_pax,
              rate: t.base_rate || t.rate,
              quantity: selection?.quantity || 1,
              // GST fields
              gst_slab: t.gst_slab ?? 5,
              includes_gst: t.includes_gst ?? false,
              transport_mode: t.transport_mode || 'Road',
            });
          }
        });
      });
      
      // Only update if changed to avoid infinite loops
      if (JSON.stringify(formData.p2p_transfers) !== JSON.stringify(selectedP2PTransfers)) {
        handleInputChange('p2p_transfers', selectedP2PTransfers.length > 0 ? selectedP2PTransfers : null);
      }
    }
  }, [p2pTransfers.available, p2pTransfers.selected]);
  
  // Initialize manual costs from formData.custom_route when component mounts
  useEffect(() => {
    if (isManualPackage && formData.custom_route?.length > 0) {
      // Only initialize if mealPlanData is empty (avoid resetting user-entered data)
      setManualCosts(prev => {
        const hasExistingData = Object.keys(prev.mealPlanData || {}).length > 0;
        
        // Initialize meal plan data only if empty
        const initialMealPlanData = hasExistingData ? prev.mealPlanData : {};
        if (!hasExistingData) {
          (formData.meal_plans || ['CP']).forEach(mealPlan => {
            initialMealPlanData[mealPlan] = {};
            formData.custom_route.forEach(stop => {
              const placeName = stop.place_name || stop.place;
              if (placeName) {
                initialMealPlanData[mealPlan][placeName] = {};
                formData.selected_categories.forEach(cat => {
                  initialMealPlanData[mealPlan][placeName][cat] = {
                    hotel_name: '',
                    room_type: '',
                    cost: '',
                  };
                });
              }
            });
          });
        }
        
        // Always update vehicle costs from vehicleOptions
        const initialVehicleCosts = {};
        const totalDays = (formData.custom_route?.reduce((sum, s) => sum + (s.nights || 0), 0) || 0) + 1;
        vehicleOptions?.forEach((v, idx) => {
          const existingCost = prev.vehicleCosts?.[v.vehicle_type];
          const total = existingCost?.total ?? ((v.rate_per_day || v.rate_amount || 0) * totalDays);
          const baseTotal = idx === 0 ? total : (prev.vehicleCosts?.[vehicleOptions[0]?.vehicle_type]?.total || (vehicleOptions[0]?.rate_per_day || vehicleOptions[0]?.rate_amount || 0) * totalDays);
          initialVehicleCosts[v.vehicle_type] = {
            total: total,
            uplift: idx === 0 ? 0 : total - baseTotal,
            rate_per_day: existingCost?.rate_per_day ?? (v.rate_per_day || v.rate_amount || 0),
            days: totalDays,
          };
        });
        
        return { 
          ...prev, 
          mealPlanData: initialMealPlanData,
          vehicleCosts: Object.keys(initialVehicleCosts).length > 0 ? initialVehicleCosts : prev.vehicleCosts,
          selectedVehicles: prev.selectedVehicles?.length > 0 ? prev.selectedVehicles : (vehicleOptions?.length > 0 ? [vehicleOptions[0]?.vehicle_type] : []),
        };
      });
    }
  }, [isManualPackage, formData.custom_route?.length, formData.selected_categories?.length, formData.meal_plans?.length, vehicleOptions?.length]);
  
  // Update manual cost for a stop (with meal plan support)
  const updateManualStopCost = (mealPlan, place, category, field, value) => {
    setManualCosts(prev => ({
      ...prev,
      mealPlanData: {
        ...prev.mealPlanData,
        [mealPlan]: {
          ...(prev.mealPlanData[mealPlan] || {}),
          [place]: {
            ...(prev.mealPlanData[mealPlan]?.[place] || {}),
            [category]: {
              ...(prev.mealPlanData[mealPlan]?.[place]?.[category] || {}),
              [field]: value,
            },
          },
        },
      },
    }));
  };
  
  // Toggle vehicle selection for manual package
  const toggleManualVehicle = (vehicleType) => {
    setManualCosts(prev => {
      const isSelected = prev.selectedVehicles.includes(vehicleType);
      // Ensure at least one vehicle is always selected (the base vehicle)
      if (isSelected && prev.selectedVehicles.length <= 1) {
        return prev; // Don't allow deselecting the last vehicle
      }
      return {
        ...prev,
        selectedVehicles: isSelected
          ? prev.selectedVehicles.filter(v => v !== vehicleType)
          : [...prev.selectedVehicles, vehicleType],
      };
    });
  };
  
  // Update vehicle cost manually
  const updateVehicleCost = (vehicleType, field, value) => {
    setManualCosts(prev => {
      const updatedVehicleCosts = {
        ...prev.vehicleCosts,
        [vehicleType]: {
          ...prev.vehicleCosts[vehicleType],
          [field]: parseFloat(value) || 0,
        },
      };
      // Recalculate uplift based on base vehicle (first selected)
      const baseVehicle = prev.selectedVehicles[0];
      if (baseVehicle && vehicleType !== baseVehicle) {
        const baseTotal = updatedVehicleCosts[baseVehicle]?.total || 0;
        updatedVehicleCosts[vehicleType].uplift = (updatedVehicleCosts[vehicleType]?.total || 0) - baseTotal;
      }
      return { ...prev, vehicleCosts: updatedVehicleCosts };
    });
  };
  
  // Get transfer cost (from first selected vehicle)
  const getManualTransferCost = () => {
    const firstSelectedVehicle = manualCosts.selectedVehicles?.[0];
    return manualCosts.vehicleCosts?.[firstSelectedVehicle]?.total || 0;
  };
  
  // Calculate manual package totals (now per meal plan)
  const calculateManualTotals = () => {
    const mealPlanTotals = {};
    const transferCost = getManualTransferCost();
    
    // Calculate totals for each meal plan
    (formData.meal_plans || ['CP']).forEach(mealPlan => {
      const mealPlanData = manualCosts.mealPlanData?.[mealPlan] || {};
      let totalHotelCost = 0;
      const categoryCosts = {};
      
      // Sum up hotel costs per category for this meal plan
      Object.entries(mealPlanData).forEach(([place, categories]) => {
        Object.entries(categories).forEach(([category, data]) => {
          const cost = parseFloat(data.cost) || 0;
          totalHotelCost += cost;
          if (!categoryCosts[category]) categoryCosts[category] = 0;
          categoryCosts[category] += cost;
        });
      });
      
      const grossTotal = totalHotelCost + transferCost;
      
      // Apply markup
      const markupPct = parseFloat(formData.markup_pct) || 0;
      const markupAmount = (grossTotal * markupPct) / 100;
      const afterMarkup = grossTotal + markupAmount;
      
      // Apply discount
      const discountPct = parseFloat(formData.discount_pct) || 0;
      const discountAmt = formData.discount_type === 'amount' 
        ? (parseFloat(formData.discount_amount) || 0)
        : (afterMarkup * discountPct) / 100;
      const afterDiscount = afterMarkup - discountAmt;
      
      // Apply GST
      const gstPct = parseFloat(formData.gst_percentage) || 0;
      const gstAmount = formData.gst_included ? (afterDiscount * gstPct) / 100 : 0;
      
      mealPlanTotals[mealPlan] = {
        hotel_cost: totalHotelCost,
        transfer_cost: transferCost,
        gross_total: grossTotal,
        markup_amount: markupAmount,
        discount_amount: discountAmt,
        gst_amount: gstAmount,
        final_price: afterDiscount + gstAmount,
        category_costs: categoryCosts,
      };
    });
    
    // Check for total override
    const totalOverride = parseFloat(manualCosts.total_override);
    const hasOverride = !isNaN(totalOverride) && totalOverride > 0;
    
    // Get first meal plan's totals as the primary
    const firstMealPlan = formData.meal_plans?.[0] || 'CP';
    const primaryTotals = mealPlanTotals[firstMealPlan] || {};
    
    return {
      ...primaryTotals,
      meal_plan_totals: mealPlanTotals,
      has_override: hasOverride,
      final_price: hasOverride ? totalOverride : primaryTotals.final_price,
      selected_vehicles: manualCosts.selectedVehicles,
      vehicle_costs: manualCosts.vehicleCosts,
    };
  };
  
  // Sync manual costs to formData so CreateQuotation can access them when saving
  useEffect(() => {
    if (isManualPackage && Object.keys(manualCosts.mealPlanData || {}).length > 0) {
      const totals = calculateManualTotals();
      console.log('Syncing manual costs to formData:', {
        mealPlanData: manualCosts.mealPlanData,
        selectedVehicles: manualCosts.selectedVehicles,
        vehicleCosts: manualCosts.vehicleCosts,
      });
      // Use the correct signature: handleInputChange(field, value)
      handleInputChange('manual_costs_data', {
        mealPlanData: manualCosts.mealPlanData,
        selectedVehicles: manualCosts.selectedVehicles,
        vehicleCosts: manualCosts.vehicleCosts,
        total_override: manualCosts.total_override,
        calculated: totals,
      });
    }
  }, [manualCosts, isManualPackage, formData.markup_pct, formData.discount_pct, formData.discount_type, formData.discount_amount, formData.gst_percentage, formData.gst_included, formData.meal_plans]);
  
  // Agency masters for Quote Content Boxes
  const [agencyMasters, setAgencyMasters] = useState([]);
  
  // Payment policies
  const [paymentPolicies, setPaymentPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(formData.payment_policy_id || null);
  
  // Fetch agency masters and payment policies on mount
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const response = await api.get('/admin/masters');
        setAgencyMasters(response.data || []);
      } catch (error) {
        console.error('Failed to fetch agency masters:', error);
      }
    };
    
    const fetchPolicies = async () => {
      try {
        const response = await api.get('/payment-policies');
        setPaymentPolicies(response.data || []);
        // Set default policy if not already set
        if (!formData.payment_policy_id) {
          const defaultPolicy = response.data?.find(p => p.is_default);
          if (defaultPolicy) {
            setSelectedPolicy(defaultPolicy.policy_id);
            handleInputChange('payment_policy_id', defaultPolicy.policy_id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch payment policies:', error);
      }
    };
    
    fetchMasters();
    fetchPolicies();
  }, []);

  // Get selected policy details
  const getSelectedPolicyDetails = () => {
    return paymentPolicies.find(p => p.policy_id === selectedPolicy);
  };
  const selectedPolicyDetails = getSelectedPolicyDetails();

  // Helper to get sorted meal plans in correct order: EP, CP, MAP, AP
  const MEAL_PLAN_ORDER = ['EP', 'CP', 'MAP', 'AP'];
  
  const getSortedMealPlanPricing = () => {
    if (!calculationResult?.meal_plan_pricing) return [];
    
    const mealPlanEntries = Object.entries(calculationResult.meal_plan_pricing);
    
    // Sort by the defined order
    return mealPlanEntries.sort((a, b) => {
      const indexA = MEAL_PLAN_ORDER.indexOf(a[0]);
      const indexB = MEAL_PLAN_ORDER.indexOf(b[0]);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
  };

  // Helper to get categories sorted by minimum price (ascending)
  const getSortedCategories = () => {
    if (!calculationResult?.recommended_hotels) return [];
    
    const categories = Object.keys(calculationResult.recommended_hotels);
    
    // Get minimum price per category from meal_plan_pricing
    const categoryPrices = {};
    categories.forEach(category => {
      let minPrice = Infinity;
      if (calculationResult.meal_plan_pricing) {
        Object.values(calculationResult.meal_plan_pricing).forEach(mealData => {
          if (mealData.category_pricing?.[category]?.net_quoted_price) {
            minPrice = Math.min(minPrice, mealData.category_pricing[category].net_quoted_price);
          }
        });
      }
      categoryPrices[category] = minPrice === Infinity ? 0 : minPrice;
    });
    
    // Sort by price ascending
    return categories.sort((a, b) => categoryPrices[a] - categoryPrices[b]);
  };

  // Add miscellaneous cost
  const addMiscCost = () => {
    const newMiscCost = {
      id: Date.now(),
      remarks: '',
      cost_type: 'total',
      amount: 0,
      currency: 'INR',
      show_amount_breakup: true,
      is_optional: true, // Default to Optional - user must check "Include" to add to total
      gst_percentage: 0, // GST rate for this item
    };
    handleInputChange('misc_costs', [...(formData.misc_costs || []), newMiscCost]);
  };

  // Update miscellaneous cost
  const updateMiscCost = (id, field, value) => {
    const updated = (formData.misc_costs || []).map(cost => 
      cost.id === id ? { ...cost, [field]: value } : cost
    );
    handleInputChange('misc_costs', updated);
  };

  // Remove miscellaneous cost
  const removeMiscCost = (id) => {
    const updated = (formData.misc_costs || []).filter(cost => cost.id !== id);
    handleInputChange('misc_costs', updated);
  };

  // Auto transfer days for Custom Packages = nights + 1 (shared by options)
  const customAutoTransferDays = (calculationResult?.total_nights || 0) + 1;

  // Shared point-to-point transfers for Custom Packages. Hand-entered here
  // because the master-driven picker only covers predefined / custom routes.
  const addManualP2P = () => {
    handleInputChange('p2p_transfers', [
      ...(formData.p2p_transfers || []),
      {
        id: Date.now(),
        from_place: '',
        to_place: '',
        transport_mode: 'Road',
        rate: 0,
        quantity: 1,
        gst_slab: 5,
        includes_gst: false,
      },
    ]);
  };

  const updateManualP2P = (id, field, value) => {
    handleInputChange(
      'p2p_transfers',
      (formData.p2p_transfers || []).map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const removeManualP2P = (id) => {
    const next = (formData.p2p_transfers || []).filter((p) => p.id !== id);
    handleInputChange('p2p_transfers', next.length > 0 ? next : null);
  };

  // Calculate total misc costs (excluding optional items)
  const calculateMiscTotal = (includeOptional = false) => {
    if (!formData.misc_costs?.length) return 0;
    return formData.misc_costs.reduce((sum, cost) => {
      // Skip optional items unless includeOptional is true
      if (cost.is_optional && !includeOptional) return sum;
      
      let amount = parseFloat(cost.amount) || 0;
      if (cost.cost_type === 'per_person') {
        amount *= (formData.no_adults + formData.no_children_with_bed + formData.no_children_no_bed);
      } else if (cost.cost_type === 'per_room') {
        const totalRooms = (calculationResult?.single_rooms || 0) + (calculationResult?.double_rooms || 0) + (calculationResult?.triple_rooms || 0);
        amount *= totalRooms;
      } else if (cost.cost_type === 'per_night') {
        amount *= (calculationResult?.total_nights || 1);
      }
      // Add GST on misc cost
      const gstPct = parseFloat(cost.gst_percentage) || 0;
      if (gstPct > 0) {
        amount += amount * (gstPct / 100);
      }
      return sum + amount;
    }, 0);
  };

  // Calculate misc cost for a single item (with GST)
  const calculateSingleMiscCost = (cost) => {
    let amount = parseFloat(cost.amount) || 0;
    if (cost.cost_type === 'per_person') {
      amount *= (formData.no_adults + formData.no_children_with_bed + formData.no_children_no_bed);
    } else if (cost.cost_type === 'per_room') {
      const totalRooms = (calculationResult?.single_rooms || 0) + (calculationResult?.double_rooms || 0) + (calculationResult?.triple_rooms || 0);
      amount *= totalRooms;
    } else if (cost.cost_type === 'per_night') {
      amount *= (calculationResult?.total_nights || 1);
    }
    // Add GST
    const gstPct = parseFloat(cost.gst_percentage) || 0;
    if (gstPct > 0) {
      amount += amount * (gstPct / 100);
    }
    return amount;
  };

  if (showOnlyPreview && calculationResult) {
    // Define variables needed for preview from component state
    const p2pAvailable = p2pTransfers.available || {};
    const p2pSelected = p2pTransfers.selected || {};
    const miscCosts = formData.misc_costs || [];
    
    // Handler for P2P toggle in preview mode
    const handleP2PTogglePreview = (p2pId) => {
      let legKey = null;
      Object.entries(p2pAvailable).forEach(([leg, transfers]) => {
        if (Array.isArray(transfers) && transfers.some(t => t.p2p_id === p2pId)) {
          legKey = leg;
        }
      });
      if (legKey) {
        toggleP2PTransfer(p2pId, legKey);
        if (setHasChanges) setHasChanges(true);
      }
    };

    // Reuse the same discount calculation logic from Step 2
    const discountType = formData.discount_type || 'percent';
    const discountPct = parseFloat(formData.discount_pct) || 0;
    const discountAmountVal = parseFloat(formData.discount_amount) || 0;
    const hasDiscount = (discountType === 'percent' && discountPct > 0) || (discountType === 'amount' && discountAmountVal > 0);
    const gstPctPreview = parseFloat(formData.gst_percentage) || 0;
    const gstApplyOnPreview = formData.gst_apply_on || 'total';

    return (
      <div className="space-y-4">
        {/* Recalculate Warning Banner */}
        {hasChanges && (
          <div
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col gap-3"
            data-testid="stale-calc-banner"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Prices are out of date</p>
                  <p className="text-sm text-amber-700">
                    {staleChanges.length > 0
                      ? 'You changed the following after the last calculation:'
                      : "You've made changes since the last calculation."}
                  </p>
                </div>
              </div>
              <Button
                onClick={onCalculate}
                disabled={calculating}
                className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                data-testid="recalculate-warning-btn"
              >
                {calculating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4 mr-2" />
                )}
                Recalculate Quote
              </Button>
            </div>
            {staleChanges.length > 0 && (
              <ul className="sm:pl-8 space-y-1" data-testid="stale-change-list">
                {staleChanges.slice(0, 6).map((change) => (
                  <li
                    key={change.key}
                    className="text-sm text-amber-900 flex items-start gap-2"
                    data-testid={`stale-change-${change.key}`}
                  >
                    <span className="text-amber-500 mt-[3px]">•</span>
                    <span>{change.text}</span>
                  </li>
                ))}
                {staleChanges.length > 6 && (
                  <li className="text-sm text-amber-700 sm:pl-4" data-testid="stale-change-more">
                    +{staleChanges.length - 6} more change
                    {staleChanges.length - 6 > 1 ? 's' : ''}
                  </li>
                )}
              </ul>
            )}
          </div>
        )}

        {/* Trip Summary */}
        <TripSummaryAccordion
          formData={formData}
          calculationResult={calculationResult}
          defaultOpen={false}
        />

        {/* DMC Package Preview - Full details matching other route types */}
        {isDMCPackage && calculationResult?.is_dmc_package && (
          <Card data-testid="dmc-calculation-result">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg text-navy flex items-center gap-2">
                <FileText className="w-5 h-5" />
                DMC Package Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Package Header */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">{calculationResult.package_title || formData.dmc_content?.parsed?.package_title || 'DMC Package'}</h3>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Badge variant="outline" className="bg-white">{calculationResult.total_days || formData.dmc_content?.parsed?.total_days || 0}D / {calculationResult.total_nights || formData.dmc_content?.parsed?.total_nights || 0}N</Badge>
                  {(calculationResult.vehicle || formData.dmc_content?.parsed?.vehicle) && (
                    <Badge variant="outline" className="bg-white"><Car className="w-3 h-3 mr-1" /> {calculationResult.vehicle || formData.dmc_content?.parsed?.vehicle}</Badge>
                  )}
                </div>
              </div>

              {/* Passenger & Room Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-white rounded-lg border border-navy/15">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Passengers</p>
                    <p className="text-xs text-gray-600">
                      {formData.no_adults} Adult{formData.no_adults !== 1 ? 's' : ''}
                      {formData.no_children_with_bed > 0 && ` • ${formData.no_children_with_bed}C(bed)`}
                      {formData.no_children_no_bed > 0 && ` • ${formData.no_children_no_bed}C(no bed)`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Bed className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Rooms</p>
                    <p className="text-xs text-gray-600">
                      {(formData.room_config?.single_rooms || calculationResult?.single_rooms || 0) > 0 && `${formData.room_config?.single_rooms || calculationResult?.single_rooms} Single`}
                      {(formData.room_config?.double_rooms || calculationResult?.double_rooms || 0) > 0 && `${(formData.room_config?.single_rooms || calculationResult?.single_rooms || 0) > 0 ? ' • ' : ''}${formData.room_config?.double_rooms || calculationResult?.double_rooms} Double`}
                      {(formData.room_config?.triple_rooms || calculationResult?.triple_rooms || 0) > 0 && ` • ${formData.room_config?.triple_rooms || calculationResult?.triple_rooms} Triple`}
                      {!(formData.room_config?.single_rooms || calculationResult?.single_rooms || formData.room_config?.double_rooms || calculationResult?.double_rooms || formData.room_config?.triple_rooms || calculationResult?.triple_rooms) && 'Auto-calculated'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Travel Date</p>
                    <p className="text-xs text-gray-600">{formData.arrival_date ? new Date(formData.arrival_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }) : 'Not set'}</p>
                  </div>
                </div>
              </div>

              {/* Route Summary for DMC Package */}
              {formData.dmc_content?.parsed?.accommodation_options?.[0]?.hotels && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Route Summary
                  </Label>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Arrival City */}
                    <div className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                      <span className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">A</span>
                      <span className="text-sm font-medium text-emerald-700">{formData.arrival_city || formData.dmc_content?.parsed?.accommodation_options?.[0]?.hotels?.[0]?.place || 'Arrival'}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    {/* Route stops */}
                    {formData.dmc_content.parsed.accommodation_options[0].hotels.map((hotel, idx) => (
                      <React.Fragment key={idx}>
                        <div className="flex items-center gap-1 bg-navy/5 border border-navy/20 rounded-full px-3 py-1">
                          <span className="w-5 h-5 bg-navy text-white rounded-full flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>
                          <span className="text-sm font-medium">{hotel.place}</span>
                          <span className="text-xs text-gray-500">({hotel.nights}N)</span>
                        </div>
                        {idx < formData.dmc_content.parsed.accommodation_options[0].hotels.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        )}
                      </React.Fragment>
                    ))}
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    {/* Departure City */}
                    <div className="flex items-center gap-1 bg-red-50 border border-red-200 rounded-full px-3 py-1">
                      <span className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">D</span>
                      <span className="text-sm font-medium text-red-700">{formData.departure_city || formData.dmc_content?.parsed?.accommodation_options?.[0]?.hotels?.[formData.dmc_content.parsed.accommodation_options[0].hotels.length - 1]?.place || 'Departure'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Hotels by Category - Separate table for each option */}
              {formData.dmc_content?.parsed?.accommodation_options && formData.dmc_content.parsed.accommodation_options.length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-4">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <Hotel className="w-4 h-4" />
                    Hotels by Category
                  </Label>
                  
                  {/* Render separate table for each option */}
                  {(formData.dmc_content?.parsed?.accommodation_options || []).map((option, optIdx) => (
                    <div key={option.option_name} className="space-y-2">
                      <h4 className="text-xs font-bold text-navy bg-navy/10 px-3 py-1.5 rounded-t-lg border border-navy/20 border-b-0">
                        {option.option_name}
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-navy/25">
                          <thead>
                            <tr className="bg-navy/90 text-white">
                              <th className="border border-navy/50 p-2 text-left font-semibold">Destination</th>
                              <th className="border border-navy/50 p-2 text-left font-semibold">Hotel</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold">Room Name</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold">Rooms</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold">Nights</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold">Dates</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(option.hotels || []).map((hotel, idx) => {
                              // Calculate dates for this hotel
                              let checkIn = hotel.check_in;
                              let checkOut = hotel.check_out;
                              if (!checkIn && formData.arrival_date) {
                                const baseDate = new Date(formData.arrival_date);
                                let cumulativeNights = 0;
                                for (let i = 0; i < idx; i++) {
                                  cumulativeNights += option.hotels[i]?.nights || 1;
                                }
                                const checkInDate = new Date(baseDate);
                                checkInDate.setDate(checkInDate.getDate() + cumulativeNights);
                                const checkOutDate = new Date(checkInDate);
                                checkOutDate.setDate(checkOutDate.getDate() + (hotel.nights || 1));
                                checkIn = checkInDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                                checkOut = checkOutDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                              }
                              
                              return (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                  <td className="border border-navy/25 p-2 font-medium">{hotel.place || hotel.destination}</td>
                                  <td className="border border-navy/25 p-2">
                                    <span className="font-medium text-navy">{hotel.hotel || '-'}</span>
                                  </td>
                                  <td className="border border-navy/25 p-2 text-center text-gray-600">{hotel.room_name || '—'}</td>
                                  <td className="border border-navy/25 p-2 text-center" data-testid={`wizard-hotel-rooms-${idx}`}>{hotelRoomsText(hotel.num_rooms || hotel.rooms_required)}</td>
                                  <td className="border border-navy/25 p-2 text-center font-medium">{hotel.nights || 1}N</td>
                                  <td className="border border-navy/25 p-2 text-center text-[10px] text-gray-600">
                                    {checkIn && checkOut ? `${checkIn} → ${checkOut}` : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing by Category - With currency conversion, GST, totals */}
              {formData.dmc_content?.parsed?.pricing_options && formData.dmc_content.parsed.pricing_options.length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Pricing by Category
                  </Label>
                  {(() => {
                    const pricingOptions = formData.dmc_content.parsed.pricing_options;
                    const noAdults = formData.no_adults || 0;
                    const noChildrenWithBed = formData.no_children_with_bed || 0;
                    const noChildrenNoBed = formData.no_children_no_bed || 0;
                    const gstPct = formData.gst_percentage || 5;
                    
                    // Get currency conversion rate
                    const firstCurrency = pricingOptions[0]?.currency || 'INR';
                    const conversionRateKey = `${firstCurrency.toLowerCase()}_to_inr_rate`;
                    const conversionRate = formData.dmc_content?.parsed?.[conversionRateKey] || 1;
                    const needsConversion = firstCurrency !== 'INR' && conversionRate > 1;
                    
                    // Determine which columns have data
                    const hasExtraBed = pricingOptions.some(opt => (opt.extra_adult || 0) > 0);
                    const hasChildWithBed = pricingOptions.some(opt => (opt.child_with_bed || 0) > 0) || noChildrenWithBed > 0;
                    const hasChildNoBed = pricingOptions.some(opt => (opt.child_no_bed || 0) > 0) || noChildrenNoBed > 0;
                    
                    // Calculate extra adults (adults beyond twin sharing capacity)
                    const twinCapacity = (formData.double_rooms || 0) * 2 + (formData.twin_rooms || 0) * 2;
                    const extraAdults = Math.max(0, noAdults - twinCapacity);
                    const adultsInRooms = noAdults - extraAdults;
                    
                    return (
                      <div className="overflow-x-auto">
                        {needsConversion && (
                          <div className="mb-2 text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded inline-block">
                            Converted from {firstCurrency} @ 1 {firstCurrency} = ₹{conversionRate.toFixed(2)} INR
                          </div>
                        )}
                        <table className="w-full text-xs border border-navy/25">
                          <thead>
                            <tr className="bg-navy text-white">
                              <th className="border border-navy/50 p-2 text-left font-semibold">Category</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold">
                                Adult ({adultsInRooms})
                              </th>
                              {(hasExtraBed || extraAdults > 0) && (
                                <th className="border border-navy/50 p-2 text-center font-semibold">
                                  Extra Bed ({extraAdults})
                                </th>
                              )}
                              {hasChildWithBed && (
                                <th className="border border-navy/50 p-2 text-center font-semibold">
                                  Child w/Bed ({noChildrenWithBed})
                                </th>
                              )}
                              {hasChildNoBed && (
                                <th className="border border-navy/50 p-2 text-center font-semibold">
                                  Child w/o Bed ({noChildrenNoBed})
                                </th>
                              )}
                              <th className="border border-navy/50 p-2 text-center font-semibold bg-blue-900">Subtotal</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold bg-blue-900">GST ({gstPct}%)</th>
                              <th className="border border-navy/50 p-2 text-center font-semibold bg-green-800">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pricingOptions.map((opt, idx) => {
                              // Apply conversion rate to all prices
                              const rate = needsConversion ? conversionRate : 1;
                              const perPerson = (opt.per_person || 0) * rate;
                              const extraAdultRate = (opt.extra_adult || 0) * rate;
                              const childWithBedRate = (opt.child_with_bed || 0) * rate;
                              const childNoBedRate = (opt.child_no_bed || 0) * rate;
                              
                              // Calculate totals
                              const adultTotal = perPerson * adultsInRooms;
                              const extraBedTotal = extraAdultRate * extraAdults;
                              const childWBTotal = childWithBedRate * noChildrenWithBed;
                              const childNBTotal = childNoBedRate * noChildrenNoBed;
                              const subtotal = adultTotal + extraBedTotal + childWBTotal + childNBTotal;
                              const gstAmount = subtotal * (gstPct / 100);
                              const total = subtotal + gstAmount;
                              
                              return (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                  <td className="border border-navy/25 p-2 font-medium">{opt.option_name}</td>
                                  <td className="border border-navy/25 p-2 text-center font-semibold text-green-700">
                                    {formatCurrency(perPerson)}
                                  </td>
                                  {(hasExtraBed || extraAdults > 0) && (
                                    <td className="border border-navy/25 p-2 text-center">
                                      {extraAdultRate > 0 ? formatCurrency(extraAdultRate) : '-'}
                                    </td>
                                  )}
                                  {hasChildWithBed && (
                                    <td className="border border-navy/25 p-2 text-center">
                                      {childWithBedRate > 0 ? formatCurrency(childWithBedRate) : '-'}
                                    </td>
                                  )}
                                  {hasChildNoBed && (
                                    <td className="border border-navy/25 p-2 text-center">
                                      {childNoBedRate > 0 ? formatCurrency(childNoBedRate) : '-'}
                                    </td>
                                  )}
                                  <td className="border border-navy/25 p-2 text-center font-semibold bg-blue-50">
                                    {formatCurrency(subtotal)}
                                  </td>
                                  <td className="border border-navy/25 p-2 text-center text-gray-600 bg-blue-50">
                                    {formatCurrency(gstAmount)}
                                  </td>
                                  <td className="border border-navy/25 p-2 text-center font-bold text-green-800 bg-green-50">
                                    {formatCurrency(total)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Itinerary */}
              {formData.dmc_content?.parsed?.itinerary && formData.dmc_content.parsed.itinerary.length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Itinerary
                  </Label>
                  <div className="space-y-2">
                    {formData.dmc_content.parsed.itinerary.map((day, idx) => (
                      <div key={idx} className="flex gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-12 h-8 bg-navy text-white rounded flex items-center justify-center text-xs font-bold shrink-0">
                          Day {day.day}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-navy">{day.title}</p>
                          {day.description && <p className="text-xs text-gray-600 mt-1">{day.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Inclusions & Exclusions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formData.dmc_content?.parsed?.inclusions && formData.dmc_content.parsed.inclusions.length > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-sm font-semibold text-green-800 flex items-center gap-2 mb-2">
                      <Check className="w-4 h-4" />
                      Inclusions
                    </Label>
                    <ul className="text-xs text-green-700 space-y-1">
                      {formData.dmc_content.parsed.inclusions.map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-green-500">✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {formData.dmc_content?.parsed?.exclusions && formData.dmc_content.parsed.exclusions.length > 0 && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <Label className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
                      <X className="w-4 h-4" />
                      Exclusions
                    </Label>
                    <ul className="text-xs text-red-700 space-y-1">
                      {formData.dmc_content.parsed.exclusions.map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-red-500">✗</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* DMC Pricing Summary */}
              {(() => {
                const dmcPricing = calculationResult.category_pricing?.DMC || {};
                const totalPax = (formData.no_adults || 0) + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0);
                // Use canonical field names with legacy fallbacks
                const baseCost = dmcPricing.gross_cost || dmcPricing.base_cost || 0;
                const totalCost = dmcPricing.total_with_gst || dmcPricing.total_cost || 0;
                return (
                  <div className="bg-navy text-white rounded-xl p-4 shadow-lg">
                    <div className="flex justify-between text-white/70 mb-2">
                      <span>Base Cost ({totalPax} pax)</span>
                      <span>{formatCurrency(baseCost)}</span>
                    </div>
                    {dmcPricing.markup_amount > 0 && <div className="flex justify-between text-white/70 mb-2"><span>+ Markup</span><span>{formatCurrency(dmcPricing.markup_amount)}</span></div>}
                    {dmcPricing.discount_amount > 0 && <div className="flex justify-between text-green-300 mb-2"><span>- Discount</span><span>-{formatCurrency(dmcPricing.discount_amount)}</span></div>}
                    <div className="flex justify-between items-center pt-3 border-t-2 border-white/30">
                      <span className="font-semibold">Grand Total</span>
                      <span className="text-2xl font-bold">{formatCurrency(totalCost)}</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Predefined/Custom Route Preview */}
        {!calculationResult?.is_custom_package && !isManualPackage && !isDMCPackage && (
          <Card data-testid="calculation-result-preview">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg text-navy flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Quote Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Passenger & Room Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-white rounded-lg border border-navy/15">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Passengers</p>
                    <p className="text-xs text-gray-600">
                      {formData.no_adults} Adult{formData.no_adults !== 1 ? 's' : ''}
                      {formData.no_children_with_bed > 0 && ` • ${formData.no_children_with_bed}C(bed)`}
                      {formData.no_children_no_bed > 0 && ` • ${formData.no_children_no_bed}C(no bed)`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Bed className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Rooms</p>
                    <p className="text-xs text-gray-600">
                      {calculationResult.single_rooms > 0 && `${calculationResult.single_rooms} Single`}
                      {calculationResult.double_rooms > 0 && `${calculationResult.single_rooms > 0 ? ' • ' : ''}${calculationResult.double_rooms} Double`}
                      {calculationResult.triple_rooms > 0 && ` • ${calculationResult.triple_rooms} Triple`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Car className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Vehicle</p>
                    <p className="text-xs text-gray-600">{calculationResult.vehicle_type || formData.base_vehicle_type || 'Auto'}</p>
                  </div>
                </div>
              </div>

              {/* REORDERED SECTION 1: Hotels by Category - With alternatives dropdown */}
              {calculationResult.recommended_hotels && Object.keys(calculationResult.recommended_hotels).length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <Hotel className="w-4 h-4" />
                    Hotels by Category
                  </Label>
                  <p className="text-xs text-gray-500">Change hotels using dropdowns. Click Recalculate to update prices.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-navy/25">
                      <thead>
                        <tr className="bg-navy text-white">
                          <th className="border border-navy/50 p-2 text-left font-semibold">Destination</th>
                          {getSortedCategories().map(cat => (
                            <th key={cat} className="border border-navy/50 p-2 text-center font-semibold min-w-[180px]">{cat}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          try {
                            const destinations = Array.from(new Set(
                              Object.values(calculationResult.recommended_hotels)
                                .filter(catHotels => catHotels && typeof catHotels === 'object')
                                .flatMap(catHotels => Object.keys(catHotels))
                            ));
                            return destinations.map((destination, placeIdx) => (
                              <tr key={destination} className={placeIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border border-navy/25 p-2 font-medium text-navy">
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    {destination}
                                  </div>
                                </td>
                                {getSortedCategories().map(category => {
                                  const hotelInfo = calculationResult.recommended_hotels[category]?.[destination] || {};
                                  const flagStyle = getFlagStyle(hotelInfo.flag);
                                  const alternatives = hotelInfo.alternatives || calculationResult?.hotel_alternatives?.[category]?.[destination] || [];
                                  
                                  // Check if there's an override - show override data if exists
                                  const override = formData.hotel_overrides?.[category]?.[destination];
                                  const displayHotel = override || hotelInfo;
                                  const displayName = displayHotel.hotel_name;
                                  const displayRoom = displayHotel.room_name || '';
                                  const displayCheckIn = displayHotel.check_in || hotelInfo.check_in;
                                  const displayCheckOut = displayHotel.check_out || hotelInfo.check_out;
                                  
                                  return (
                                    <td key={category} className={`border border-navy/25 p-2 ${override ? 'bg-amber-50' : flagStyle.bg}`}>
                                      {displayName ? (
                                        <div className="space-y-1">
                                          {/* Hotel Name with Flag/Override indicator */}
                                          <div className="flex items-center gap-1">
                                            {override && (
                                              <span className="text-xs font-bold text-amber-600" title="Changed">★</span>
                                            )}
                                            {!override && flagStyle && hotelInfo.flag && hotelInfo.flag !== 'match' && !hotelInfo.resolved_tier && (
                                              <span className={`text-xs font-bold ${flagStyle.text}`} title={flagStyle.label}>
                                                {flagStyle.icon}
                                              </span>
                                            )}
                                            <span className="font-medium text-xs">{displayName}</span>
                                          </div>
                                          {/* Room Name */}
                                          <div className="text-[10px] text-green-600">
                                            {displayRoom || '—'}
                                          </div>
                                          {!override && hotelInfo.flag && hotelInfo.flag !== 'match' && hotelInfo.resolved_tier && (
                                            <div className="inline-block text-[10px] font-medium rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                                              title={`No ${String(category).trim()} hotel in ${destination}`}
                                              data-testid={`wizard-hotel-borrowed-${String(category).trim()}-${destination}`}>
                                              {String(hotelInfo.flag).startsWith('downgrade') ? '↓' : '↑'} From {String(hotelInfo.resolved_tier).trim()} – no {String(category).trim()} hotel here
                                            </div>
                                          )}
                                          <div className="text-[10px] text-gray-600" data-testid={`wizard-hotel-rooms-${String(category).trim()}-${destination}`}>
                                            Rooms: {hotelRoomsText(displayHotel.num_rooms || displayHotel.rooms_required)}
                                          </div>
                                          {/* Check-in/Check-out dates */}
                                          {(displayCheckIn || displayCheckOut) && (
                                            <div className="text-[10px] text-gray-500">
                                              {displayCheckIn && new Date(displayCheckIn + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                              {displayCheckIn && displayCheckOut && ' → '}
                                              {displayCheckOut && new Date(displayCheckOut + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                            </div>
                                          )}
                                          {/* Alternatives Dropdown */}
                                          {alternatives.length > 0 && (
                                            <div className="mt-1 pt-1 border-t border-gray-200">
                                              <Select
                                                value=""
                                                onValueChange={(hotelId) => {
                                                  const selectedAlt = alternatives.find(a => a.hotel_id === hotelId);
                                                  if (selectedAlt) {
                                                    const overrides = { ...(formData.hotel_overrides || {}) };
                                                    if (!overrides[category]) overrides[category] = {};
                                                    overrides[category][destination] = {
                                                      hotel_id: selectedAlt.hotel_id,
                                                      hotel_name: selectedAlt.hotel_name,
                                                      room_type: selectedAlt.room_type,
                                                      room_name: selectedAlt.room_name,
                                                      rate_per_night: selectedAlt.rate_per_night,
                                                      check_in: hotelInfo.check_in,
                                                      check_out: hotelInfo.check_out,
                                                      nights: hotelInfo.nights
                                                    };
                                                    handleInputChange('hotel_overrides', overrides);
                                                    if (setHasChanges) setHasChanges(true);
                                                  }
                                                }}
                                              >
                                                <SelectTrigger className="h-6 text-[10px] bg-blue-50 border-blue-200 hover:bg-blue-100">
                                                  <SelectValue placeholder={`${alternatives.length} more options`} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {alternatives.map((alt, idx) => (
                                                    <SelectItem key={alt.hotel_id || idx} value={alt.hotel_id} className="text-xs">
                                                      <div className="flex flex-col">
                                                        <span className="font-medium">{alt.hotel_name}</span>
                                                        <span className="text-[10px] text-gray-500">{alt.room_name || '—'}</span>
                                                      </div>
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-red-400 text-xs">Not Available</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          } catch (e) {
                            console.error('Error rendering hotels table:', e);
                            return null;
                          }
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* REORDERED SECTION 2: Vehicles / Transfer Uplift - Use calculationResult.vehicle_upgrade_options */}
              {(() => {
                // Use vehicle_upgrade_options from calculationResult if available, otherwise fall back to vehicleOptions prop
                const allVehicles = calculationResult?.vehicle_upgrade_options || vehicleOptions || [];
                if (allVehicles.length === 0) return null;
                
                return (
                  <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                    <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Vehicles / Transfer Uplift
                    </Label>
                    <p className="text-xs text-gray-500">Select the base vehicle (radio). Check vehicles to include as options.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border border-navy/20">
                        <thead>
                          <tr className="bg-gray-50 border-b border-navy/20">
                            <th className="p-2 w-16 text-center text-navy font-semibold">Base</th>
                            <th className="p-2 w-10 text-center"></th>
                            <th className="p-2 text-left text-navy font-semibold">Vehicle Type</th>
                            <th className="p-2 text-center text-navy font-semibold">No. of Vehicles</th>
                            {!hideVehicleCosts && (
                              <>
                                <th className="p-2 text-center text-navy font-semibold">Days</th>
                                <th className="p-2 text-right text-navy font-semibold">Rate/Day</th>
                                <th className="p-2 text-right text-navy font-semibold">Total</th>
                              </>
                            )}
                            <th className="p-2 text-right text-navy font-semibold">Uplift</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allVehicles.map((v, idx) => {
                            const baseVehicleType = formData.base_vehicle_type || calculationResult?.vehicle_type || allVehicles[0]?.vehicle_type;
                            const isBaseVehicle = v.vehicle_type === baseVehicleType;
                            const isSelected = isBaseVehicle || selectedVehicles.includes(v.vehicle_type);
                            const days = calculationResult?.transfer_days || formData.no_nights + 1 || 0;
                            const rate = v.rate_per_day || v.rate_amount || v.rate || 0;
                            const count = v.vehicle_count || v.count || calculationResult?.vehicle_count || 1;
                            // Bug 10 fix (2026-02): the Total column shows the vehicle's
                            // own cost (rate × days × count) — same as the Save view.
                            const total = rate * days * count;

                            const baseVehicle = allVehicles.find(veh => veh.vehicle_type === baseVehicleType) || allVehicles[0];
                            // Uplift: GST + markup-inclusive delta, same rule as saved view + PDF.
                            const uplift = vehicleUplift(v, baseVehicle, rawVehicleTotalWithGst(days));
                            
                            return (
                              <tr key={idx} className={`border-b border-navy/10 ${isBaseVehicle ? 'bg-blue-50' : isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                                <td className="p-2 text-center">
                                  <input
                                    type="radio"
                                    name="base_vehicle_preview"
                                    checked={isBaseVehicle}
                                    onChange={() => {
                                      handleInputChange('base_vehicle_type', v.vehicle_type);
                                      handleInputChange('vehicle_type_selected', v.vehicle_type);
                                      if (!selectedVehicles.includes(v.vehicle_type) && onVehicleSelectionChange) {
                                        onVehicleSelectionChange(v.vehicle_type, true);
                                      }
                                      if (setHasChanges) setHasChanges(true);
                                    }}
                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isBaseVehicle}
                                    onCheckedChange={(checked) => {
                                      if (onVehicleSelectionChange) {
                                        onVehicleSelectionChange(v.vehicle_type, checked);
                                      }
                                      if (setHasChanges) setHasChanges(true);
                                    }}
                                    className="h-4 w-4"
                                  />
                                </td>
                                <td className="p-2 font-medium">
                                  {v.vehicle_label || v.label || v.vehicle_type}
                                  {isBaseVehicle && <span className="ml-2 text-blue-600 text-[10px] font-semibold">(BASE)</span>}
                                </td>
                                <td className="p-2 text-center">{count}</td>
                                {!hideVehicleCosts && (
                                  <>
                                    <td className="p-2 text-center">{days}</td>
                                    <td className="p-2 text-right">{formatCurrency(rate)}/day</td>
                                    <td className="p-2 text-right font-medium">{formatCurrency(total)}</td>
                                  </>
                                )}
                                <td className="p-2 text-right">
                                  {isBaseVehicle ? (
                                    <span className="text-blue-600 font-medium">Included</span>
                                  ) : uplift > 0 ? (
                                    <span className="text-orange-600">+ {formatCurrency(uplift)}</span>
                                  ) : uplift < 0 ? (
                                    <span className="text-green-600">Save {formatCurrency(Math.abs(uplift))}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* P2P Transfers - Full section with mode indicator */}
              {(p2pTransfers.detectedMode === 'LEG_BASED' || p2pTransfers.detectedMode === 'HYBRID' || Object.keys(p2pAvailable).length > 0) && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <Ship className="w-4 h-4" />
                    Point-to-Point Transfers
                  </Label>
                  
                  {/* Transfer Mode Indicator */}
                  <div className={`p-3 rounded-lg text-sm flex items-start gap-3 ${
                    p2pTransfers.detectedMode === 'LEG_BASED' 
                      ? 'bg-amber-50 border border-amber-200' 
                      : p2pTransfers.detectedMode === 'HYBRID'
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className={`p-1.5 rounded-full ${
                      p2pTransfers.detectedMode === 'LEG_BASED' ? 'bg-amber-100' : 
                      p2pTransfers.detectedMode === 'HYBRID' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {p2pTransfers.detectedMode === 'LEG_BASED' ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      ) : p2pTransfers.detectedMode === 'HYBRID' ? (
                        <Info className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Car className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        <span className={p2pTransfers.detectedMode === 'LEG_BASED' ? 'text-amber-800' : p2pTransfers.detectedMode === 'HYBRID' ? 'text-blue-800' : 'text-gray-800'}>
                          {p2pTransfers.modeLabel}
                        </span>
                        {p2pTransfers.hasP2pCoverage && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200">
                            {p2pTransfers.p2pCoveragePercent}% P2P Coverage
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{p2pTransfers.recommendedAction}</p>
                    </div>
                  </div>
                  
                  {/* P2P Transfers List */}
                  <div className="space-y-4">
                    {Object.entries(p2pAvailable).map(([legKey, transfers]) => {
                      if (!transfers || transfers.length === 0) return null;
                      
                      const baseTransfer = getBaseTranfer(legKey);
                      const baseRate = baseTransfer ? (baseTransfer.base_rate || baseTransfer.rate || 0) : 0;
                      
                      return (
                        <div key={legKey} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm font-medium text-navy">
                                <MapPin className="w-4 h-4" />
                                <span>{legKey.replace('->', ' → ')}</span>
                              </div>
                              <span className="text-xs text-gray-500">{transfers.length} option{transfers.length > 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          
                          <div className="divide-y divide-gray-100">
                            {transfers.map((t) => {
                              const selection = p2pSelected[t.p2p_id];
                              const isSelected = selection?.selected === true;
                              const rate = t.base_rate || t.rate || 0;
                              const pax = formData.no_adults + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0);
                              const total = t.pricing_type === 'PER_PERSON' ? rate * pax : rate;
                              const isMandatory = t.inclusion_type === 'Mandatory' || t.show_as === 'Mandatory';
                              const isBase = baseTransfer && t.p2p_id === baseTransfer.p2p_id;
                              const uplift = isBase ? 0 : (rate - baseRate);
                              
                              return (
                                <div 
                                  key={t.p2p_id} 
                                  className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                                    isSelected 
                                      ? isBase ? 'bg-green-50 border-l-4 border-green-500' : 'bg-blue-50 border-l-4 border-blue-500'
                                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                                  }`}
                                  onClick={() => handleP2PTogglePreview(t.p2p_id)}
                                >
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected 
                                      ? isBase ? 'border-green-500 bg-green-500' : 'border-blue-500 bg-blue-500'
                                      : 'border-gray-300'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-sm text-gray-900">
                                        {t.vehicle_type || t.transport_mode || 'Transfer'}
                                        {t.variant_name && <span className="text-gray-500"> - {t.variant_name}</span>}
                                      </span>
                                      {isBase && isMandatory && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700 uppercase">Base</span>
                                      )}
                                      {!isBase && isMandatory && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 uppercase">Upgrade</span>
                                      )}
                                      {!isMandatory && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600">Optional</span>
                                      )}
                                    </div>
                                    {t.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{t.description}</p>}
                                  </div>
                                  
                                  {hideVehicleCosts ? (
                                    // B2B: Uplift only — no rate, unit or Total.
                                    (() => {
                                      const perUnit = t.pricing_type === 'PER_PERSON' ? pax : 1;
                                      const tripUplift = isMandatory ? uplift * perUnit : total;
                                      return (
                                        <div className="text-right flex-shrink-0" data-testid={`p2p-uplift-${t.p2p_id}`}>
                                          {isBase && isMandatory ? (
                                            <div className="text-sm font-semibold text-green-700">Included</div>
                                          ) : tripUplift > 0 ? (
                                            <div className="text-sm font-semibold text-blue-700">+ {formatCurrency(tripUplift)}</div>
                                          ) : tripUplift < 0 ? (
                                            <div className="text-sm font-semibold text-green-600">Save {formatCurrency(Math.abs(tripUplift))}</div>
                                          ) : (
                                            <div className="text-sm text-gray-400">-</div>
                                          )}
                                          <div className="text-[10px] text-gray-400">Uplift</div>
                                        </div>
                                      );
                                    })()
                                  ) : (
                                  <div className="text-right flex-shrink-0">
                                    {isBase && isMandatory ? (
                                      <div className="text-sm font-semibold text-green-700">{formatCurrency(rate)}</div>
                                    ) : uplift > 0 && isMandatory ? (
                                      <div className="text-sm font-semibold text-blue-700">+ {formatCurrency(uplift)}</div>
                                    ) : (
                                      <div className="text-sm font-semibold text-gray-700">{formatCurrency(rate)}</div>
                                    )}
                                    <div className="text-[10px] text-gray-400">{t.pricing_type === 'PER_PERSON' ? '/person' : '/vehicle'}</div>
                                    {isSelected && <div className="text-xs font-medium text-navy mt-1">Total: {formatCurrency(total)}</div>}
                                  </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* P2P Total */}
                    {calculateP2PCost() > 0 && (
                      <div className="flex justify-end items-center gap-3 pt-2 border-t border-navy/10">
                        <span className="text-sm font-medium text-gray-600">Point-to-Point Total:</span>
                        <span className="text-lg font-bold text-navy">{formatCurrency(calculateP2PCost())}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Miscellaneous Costs - Editable */}
              <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Miscellaneous Costs
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="show-misc-table-preview"
                        checked={formData.show_misc_table !== false}
                        onCheckedChange={(checked) => handleInputChange('show_misc_table', checked)}
                      />
                      <Label htmlFor="show-misc-table-preview" className="text-xs cursor-pointer">Show as Table</Label>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addMiscCost}
                      className="h-8 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Cost
                    </Button>
                  </div>
                </div>
                
                {miscCosts.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No miscellaneous costs added</p>
                ) : (
                  <div className="space-y-3">
                    {miscCosts.map((cost, idx) => {
                      // #289720: Included/Optional toggle shown for every category / meal-plan
                      // combination (was single-option only, so it vanished with CP + MAP).
                      const isSingleOption = true;
                      const showAsOptional = cost.is_optional;
                      
                      return (
                        <div key={cost.id} className={`p-3 bg-white rounded border ${showAsOptional ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
                          <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                            {isSingleOption ? (
                              <div className="w-full sm:w-24 lg:w-24 flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!cost.is_optional}
                                    onChange={(e) => updateMiscCost(cost.id, 'is_optional', !e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                  />
                                  <span className={`text-xs font-medium ${!cost.is_optional ? 'text-green-700' : 'text-amber-700'}`}>
                                    {!cost.is_optional ? 'Included' : 'Optional'}
                                  </span>
                                </label>
                              </div>
                            ) : (
                              <div className="w-full sm:w-24 lg:w-24 flex items-center">
                                <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700 border border-amber-300">Optional</span>
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Description</Label>
                              <Input
                                placeholder="e.g., Entry tickets, Guide charges"
                                value={cost.remarks}
                                onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                                className="h-9 text-sm"
                              />
                            </div>
                            
                            <div className="w-full sm:w-28 lg:w-28">
                              <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Type</Label>
                              <Select value={cost.cost_type} onValueChange={(v) => updateMiscCost(cost.id, 'cost_type', v)}>
                                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {COST_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="w-full sm:w-24 lg:w-24">
                              <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Amount</Label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={cost.amount}
                                onChange={(e) => updateMiscCost(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                                className="h-9 text-sm"
                              />
                            </div>
                            
                            <div className="w-full sm:w-20 lg:w-20">
                              <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">GST</Label>
                              <Select value={String(cost.gst_percentage || 0)} onValueChange={(v) => updateMiscCost(cost.id, 'gst_percentage', parseFloat(v))}>
                                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="GST" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">No GST</SelectItem>
                                  <SelectItem value="5">5%</SelectItem>
                                  <SelectItem value="12">12%</SelectItem>
                                  <SelectItem value="18">18%</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeMiscCost(cost.id)}
                              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {miscCosts.length > 0 && (
                      <div className="flex justify-between items-center pt-2 border-t border-navy/15">
                        <span className="text-sm font-medium text-navy">Total Miscellaneous</span>
                        <span className="text-sm font-bold text-navy">{formatCurrency(calculateMiscTotal(true))}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* REORDERED SECTION 5: Prices by Category - MOVED AFTER MISC COSTS */}
              {calculationResult.meal_plan_pricing && Object.keys(calculationResult.meal_plan_pricing).length > 0 && (() => {
                // ────────────────────────────────────────────────────────────
                // Bug 2 + 3 + 4 (2026-02): the Prices by Category preview is
                // format-aware and toggle-aware. Rules locked with the user:
                //
                //   • quotation_format='total'         → Total Package columns
                //     - show_breakup=ON adds Hotels/Transfers (+Misc/P2P when >0)
                //   • quotation_format='person_wise'   → Per Person columns
                //     - Adult always, Child(Bed)/Child(NoBed)/Infant only if pax > 0
                //   • show_discount_separately=ON      → Discount column visible
                //     (red negative). Base column is pre-discount (net + discount).
                //   • show_discount_separately=OFF     → Discount column hidden.
                //     Base column is post-discount (net). Prices customer sees
                //     already reflect the reduction.
                //   • gst_included=true                → GST column hidden,
                //     footnote "GST @ X% included in package price" is shown.
                //   • gst_included=false && gst_pct>0  → GST column visible.
                //
                // All numbers come from backend PricingCalculator (Q4 zero-
                // tolerance policy). No re-math in JS.
                // ────────────────────────────────────────────────────────────
                const isPersonWise = ['person_wise', 'per_person'].includes(formData.quotation_format);
                // B2B always see Hotel/Transfer (markup-distributed by the API).
                const showBreakupCols = (isB2BAgent || formData.show_breakup !== false) && !isPersonWise;
                const showDiscountCol = hasDiscount && !!formData.show_discount_separately;
                const showGstCol = gstPctPreview > 0 && !formData.gst_included;
                const gstIncludedFootnote = gstPctPreview > 0 && !!formData.gst_included;
                // Ticket #285275 (Option A): "included" now adds GST to the total
                // (stored gst_amount > 0); legacy included quotes have gst_amount 0.
                const gstInTotalPreview = gstIncludedFootnote && Object.values(calculationResult.meal_plan_pricing || {})
                  .some(mp => Object.values(mp?.category_pricing || {}).some(cp => Number(cp?.gst_amount || 0) > 0));
                // Included misc is pass-through: shown only in the summary below each table.
                const includedMisc = calculateMiscTotal(false);
                const p2pTotalAll = Number(calculationResult?.p2p_total || 0);
                const showP2PCol = showBreakupCols && p2pTotalAll > 0;
                const showChildBedCol = isPersonWise && (formData.no_children_with_bed || 0) > 0;
                const showChildNoBedCol = isPersonWise && (formData.no_children_no_bed || 0) > 0;
                const showInfantCol = isPersonWise && (formData.no_infants || 0) > 0;
                return (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3" data-testid="prices-by-category">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Prices by Category
                    <span className="ml-auto text-[10px] font-normal text-navy/60 bg-navy/5 px-2 py-0.5 rounded-full" data-testid="preview-format-badge">
                      {isPersonWise ? 'Per Person' : 'Total Package'}
                      {showBreakupCols && ' · Breakup'}
                    </span>
                  </Label>
                  {getSortedMealPlanPricing().map(([mealPlan, mealData]) => {
                    // Bug 2.2: rows dropped because selected hotels lack the meal_plan rate
                    const missingForThisPlan = (calculationResult.missing_meal_plans || [])
                      .filter(m => m.meal_plan === mealPlan);
                    return (
                    <div key={mealPlan} className="border rounded-lg overflow-hidden">
                      <div className="bg-navy text-white p-2 text-sm font-semibold">
                        {MEAL_PLANS.find(m => m.value === mealPlan)?.label || mealPlan}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="border-b p-2 text-left text-navy font-semibold">Category</th>
                              {!isPersonWise && showBreakupCols && (
                                <>
                                  <th className="border-b p-2 text-right text-navy font-semibold">Hotels</th>
                                  <th className="border-b p-2 text-right text-navy font-semibold">Transfers</th>
                                  {showP2PCol && <th className="border-b p-2 text-right text-navy font-semibold">P2P</th>}
                                </>
                              )}
                              {isPersonWise && (
                                <>
                                  <th className="border-b p-2 text-right text-navy font-semibold">Adult</th>
                                  {showChildBedCol && <th className="border-b p-2 text-right text-navy font-semibold">Child(Bed)</th>}
                                  {showChildNoBedCol && <th className="border-b p-2 text-right text-navy font-semibold">Child(No Bed)</th>}
                                  {showInfantCol && <th className="border-b p-2 text-right text-navy font-semibold">Infant</th>}
                                </>
                              )}
                              {!isPersonWise && (
                                <th className="border-b p-2 text-right text-navy font-semibold">{formData.show_discount_separately ? 'Base' : 'Package'}</th>
                              )}
                              {showDiscountCol && <th className="border-b p-2 text-right text-red-600 font-semibold">Discount</th>}
                              {showGstCol && (
                                <th className="border-b p-2 text-right text-navy font-semibold">GST ({gstPctPreview}%)</th>
                              )}
                              <th className="border-b p-2 text-right text-navy font-semibold bg-navy/5">Grand Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSortedCategories()
                              .filter(category => mealData.category_pricing?.[category])
                              .map(category => {
                              const pricing = mealData.category_pricing?.[category] || {};
                              const rowDiscount = Number(pricing.discount_amount || 0);
                              const gstAmount = Number(pricing.gst_amount || 0);
                              // Backend net_quoted_price is ALREADY post-discount.
                              // When we want to show discount separately, the Base
                              // column must display the pre-discount amount so the
                              // reduction is visually clear.
                              const netPostDiscount = Number(pricing.net_quoted_price || 0) + p2pTotalAll;
                              const baseValue = showDiscountCol ? (netPostDiscount + rowDiscount) : netPostDiscount;
                              const grandTotal = Number(pricing.total_with_gst || 0) + p2pTotalAll;
                              // Bug D3 fix (2026-02): Hotel/Transfer cells now include
                              // proportional markup (matching Save + PDF). Previously
                              // the Preview showed raw pre-markup values while Save/PDF
                              // showed markup-distributed values, producing a visual
                              // reconcile mismatch (Hotels + Transfers ≠ Base in Preview).
                              const rawHotel = Number(pricing.hotel_cost || 0);
                              const rawTransfer = Number(pricing.transfer_cost || 0);
                              const markupAmt = Number(pricing.markup_amount || 0);
                              const rawGross = rawHotel + rawTransfer;
                              let hotelCost = rawHotel;
                              let transferCost = rawTransfer;
                              if (markupAmt > 0 && rawGross > 0) {
                                hotelCost = rawHotel + (markupAmt * (rawHotel / rawGross));
                                transferCost = rawTransfer + (markupAmt * (rawTransfer / rawGross));
                              }
                              // When the discount is baked into the Total column (i.e.
                              // show_discount_separately=OFF), distribute the discount
                              // proportionally into Hotel/Transfer too so the breakup
                              // reconciles: Hotel + Transfer = Total.
                              if (!showDiscountCol && rowDiscount > 0 && (hotelCost + transferCost) > 0) {
                                const sumHT = hotelCost + transferCost;
                                hotelCost = hotelCost - (rowDiscount * (hotelCost / sumHT));
                                transferCost = transferCost - (rowDiscount * (transferCost / sumHT));
                              }
                              const p2pCost = Number(pricing.p2p_cost || p2pTotalAll || 0);
                              return (
                                <tr key={category} className="border-b hover:bg-gray-50" data-testid={`pricing-row-${category}`}>
                                  <td className="p-2 font-medium">{category}</td>
                                  {!isPersonWise && showBreakupCols && (
                                    <>
                                      <td className="p-2 text-right" data-testid={`pricing-hotels-${category}`}>{formatCurrency(hotelCost)}</td>
                                      <td className="p-2 text-right" data-testid={`pricing-transfers-${category}`}>{formatCurrency(transferCost)}</td>
                                      {showP2PCol && <td className="p-2 text-right" data-testid={`pricing-p2p-${category}`}>{formatCurrency(p2pCost)}</td>}
                                    </>
                                  )}
                                  {isPersonWise && (
                                    <>
                                      <td className="p-2 text-right" data-testid={`pricing-adult-${category}`}>{formatCurrency(pricing.adult_price || pricing.per_person_price || 0)}</td>
                                      {showChildBedCol && <td className="p-2 text-right" data-testid={`pricing-child-bed-${category}`}>{formatCurrency(pricing.child_with_bed_price || 0)}</td>}
                                      {showChildNoBedCol && <td className="p-2 text-right" data-testid={`pricing-child-nobed-${category}`}>{formatCurrency(pricing.child_no_bed_price || 0)}</td>}
                                      {showInfantCol && <td className="p-2 text-right" data-testid={`pricing-infant-${category}`}>{formatCurrency(pricing.infant_price || 0)}</td>}
                                    </>
                                  )}
                                  {!isPersonWise && (
                                    <td className="p-2 text-right" data-testid={`pricing-base-${category}`}>{formatCurrency(baseValue)}</td>
                                  )}
                                  {showDiscountCol && <td className="p-2 text-right text-red-600" data-testid={`pricing-discount-${category}`}>-{formatCurrency(rowDiscount)}</td>}
                                  {showGstCol && (
                                    <td className="p-2 text-right" data-testid={`pricing-gst-${category}`}>{formatCurrency(gstAmount)}</td>
                                  )}
                                  <td className="p-2 text-right font-bold text-navy bg-navy/5" data-testid={`pricing-grand-total-${category}`}>{formatCurrency(grandTotal)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {includedMisc > 0 && (
                        <div className="p-2">
                          <MiscSummaryTable
                            testId={`misc-summary-${mealPlan}`}
                            title={`Summary – ${MEAL_PLANS.find(m => m.value === mealPlan)?.label || mealPlan}`}
                            rows={getSortedCategories().filter(c => mealData.category_pricing?.[c]).map((c) => {
                              const pkg = Number(mealData.category_pricing[c].total_with_gst || 0) + p2pTotalAll;
                              return { option: `${c} · ${mealPlan}`, package: formatCurrency(pkg), misc: formatCurrency(includedMisc), grand: formatCurrency(pkg + includedMisc) };
                            })}
                          />
                        </div>
                      )}
                      {gstIncludedFootnote && (
                        <div className="px-3 py-2 text-xs text-navy/70 bg-navy/5 border-t border-navy/10" data-testid={`gst-included-note-${mealPlan}`}>
                          {gstInTotalPreview
                            ? `GST @ ${gstPctPreview}% included in Total Package cost`
                            : `GST @ ${gstPctPreview}% included in package price`}
                        </div>
                      )}
                      {missingForThisPlan.length > 0 && (
                        <div
                          className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-200"
                          data-testid={`missing-meal-plan-footnote-${mealPlan}`}
                        >
                          <span className="font-semibold">Note:</span>{' '}
                          {missingForThisPlan.map((m, idx) => {
                            const places = (m.places || []).filter(Boolean).join(', ');
                            return (
                              <span key={`${m.category}-${idx}`}>
                                {idx > 0 && '; '}
                                {mealPlan} not available for <b>{m.category}</b>
                                {places ? ` (selected ${places} hotel${(m.places || []).length > 1 ? 's' : ''} lack ${mealPlan} rate)` : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  {(() => {
                    // One category in one meal plan: dark total panel (package + misc = grand total).
                    const plans = getSortedMealPlanPricing();
                    const cats = Object.values(plans[0]?.[1]?.category_pricing || {});
                    if (plans.length !== 1 || cats.length !== 1) return null;
                    return (
                      <PackageTotalPanel
                        packageTotal={Number(cats[0].total_with_gst || 0) + p2pTotalAll}
                        misc={includedMisc}
                        formatCurrency={formatCurrency}
                      />
                    );
                  })()}
                  {(() => {
                    // Bug 2.2: footnote for meal plans that were ENTIRELY skipped
                    const shownMps = new Set(getSortedMealPlanPricing().map(([mp]) => mp));
                    const missingList = calculationResult.missing_meal_plans || [];
                    const skippedByMp = {};
                    missingList.forEach((m) => {
                      if (m && m.meal_plan && !shownMps.has(m.meal_plan)) {
                        (skippedByMp[m.meal_plan] = skippedByMp[m.meal_plan] || []).push(m);
                      }
                    });
                    return Object.entries(skippedByMp).map(([mp, entries]) => (
                      <div
                        key={`skipped-${mp}`}
                        className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded"
                        data-testid={`missing-meal-plan-footnote-${mp}`}
                      >
                        <span className="font-semibold">Note:</span> {mp} not available for:{' '}
                        {entries.map((m, idx) => {
                          const places = (m.places || []).filter(Boolean).join(', ');
                          return (
                            <span key={`${m.category}-${idx}`}>
                              {idx > 0 && '; '}
                              <b>{m.category}</b>
                              {places ? ` (selected ${places} hotel${(m.places || []).length > 1 ? 's' : ''} lack ${mp} rate)` : ''}
                            </span>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Manual Package Preview - Shows entered package details */}
        {isManualPackage && calculationResult?.is_manual_package && (
          <Card data-testid="manual-package-preview-step3">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-navy flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Manual Package Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Package Summary */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-emerald-800">Manual Package</span>
                  <Badge className="bg-emerald-600 text-white">
                    {calculationResult.total_days || 0}D / {calculationResult.total_nights || 0}N
                  </Badge>
                </div>
                <p className="text-sm text-emerald-700 mt-1">
                  {calculationResult.total_pax || 0} Passengers
                </p>
                {/* Route Summary */}
                {formData.manual_package_data?.route?.length > 0 && (
                  <p className="text-sm text-emerald-600 mt-2">
                    <span className="font-medium">Route:</span>{' '}
                    {formData.manual_package_data.route
                      .filter(s => s.place_name)
                      .map(s => `${s.place_name} (${s.nights}N)`)
                      .join(' → ')}
                  </p>
                )}
              </div>

              {/* Options Pricing Table - Using shared MealPlanPricingTable */}
              {calculationResult.category_pricing && Object.keys(calculationResult.category_pricing).length > 0 && (() => {
                // Helper functions for MealPlanPricingTable
                const mealPlanKey = formData.meal_plans?.[0] || 'CP';
                
                const getMealPlanLabel = (mp) => {
                  const labels = { 'EP': 'Room Only', 'CP': 'Breakfast Only', 'MAP': 'Breakfast + Dinner', 'AP': 'All Meals' };
                  return labels[mp] || mp;
                };
                
                const getPaxConfig = () => ({
                  adults: formData.no_adults || 0,
                  adultsInRooms: formData.no_adults || 0,
                  extraBed: 0,
                  childWithBed: formData.no_children_with_bed || 0,
                  childNoBed: formData.no_children_no_bed || 0,
                  infants: formData.no_infants || 0
                });
                
                const getGstConfig = () => ({
                  percentage: formData.gst_percentage || 5,
                  included: formData.gst_included !== false,
                  applyOn: formData.gst_apply_on || 'total'
                });
                
                const transformCategoriesForPricing = (categoryPricing) => {
                  if (!categoryPricing) return [];
                  return Object.entries(categoryPricing).map(([catName, pricing]) => ({
                    name: catName,
                    hotelCost: pricing.hotel_cost || pricing.accommodation_total || 0,
                    transfers: (pricing.transfer_cost || 0) + (pricing.p2p_cost || 0),
                    totalPackage: pricing.net_quoted_price || pricing.total_cost || 0,
                    net_quoted_price: pricing.net_quoted_price || 0,
                    discount: pricing.discount_amount || 0,
                    markup_amount: pricing.markup_amount || 0,
                    gst_amount: pricing.gst_amount || 0,
                    total_with_gst: pricing.total_with_gst || pricing.total_cost || 0,
                    adultPrice: pricing.adult_price || pricing.per_person_price || 0,
                    extraBedPrice: pricing.adult_extra_bed_price || 0,
                    childBedPrice: pricing.child_with_bed_price || 0,
                    childNoBedPrice: pricing.child_no_bed_price || 0,
                    infantPrice: pricing.infant_price || 0,
                  }));
                };
                
                const categories = transformCategoriesForPricing(calculationResult.category_pricing);
                if (categories.length === 0) return null;
                
                return (
                  <MealPlanPricingTable
                    mealPlan={mealPlanKey}
                    mealPlanLabel={getMealPlanLabel(mealPlanKey)}
                    categories={categories}
                    format={formData.quotation_format || 'total_amount'}
                    showBreakup={isB2BAgent || formData.show_breakup !== false}
                    paxConfig={getPaxConfig()}
                    gstConfig={getGstConfig()}
                    variant="preview"
                  />
                );
              })()}

              {/* Accommodation Details */}
              {formData.manual_package_data?.options?.length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <Hotel className="w-4 h-4" />
                    Accommodation Details
                  </Label>
                  {formData.manual_package_data.options.map((opt, optIdx) => (
                    <div key={optIdx} className="border border-slate-200 rounded p-3">
                      <h5 className="font-semibold text-slate-800 mb-2">{opt.option_name}</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-slate-200">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="p-2 text-left">Place</th>
                              <th className="p-2 text-left">Hotel</th>
                              <th className="p-2 text-left">Room Name</th>
                              <th className="p-2 text-center">Nights</th>
                              <th className="p-2 text-center">Rooms</th>
                              <th className="p-2 text-right">Cost/Rm/Nt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(opt.accommodation || []).map((acc, accIdx) => (
                              <tr key={accIdx} className="border-t border-slate-200">
                                <td className="p-2 font-medium">
                                  {acc.place_name || acc.place || acc.destination || 
                                   formData.manual_package_data?.route?.[accIdx]?.place_name || 
                                   `Stop ${accIdx + 1}`}
                                </td>
                                <td className="p-2">{acc.hotel_name || '-'}</td>
                                <td className="p-2">{acc.room_name || '—'}</td>
                                <td className="p-2 text-center">{acc.nights || 1}</td>
                                <td className="p-2 text-center">{acc.num_rooms || 1}</td>
                                <td className="p-2 text-right">{formatCurrency(acc.room_rate_per_night || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {opt.package_transfer?.vehicle_name && (
                        <div className="mt-2 text-sm text-slate-600">
                          <span className="font-medium">Transfer:</span> {opt.package_transfer.vehicle_name} × {opt.package_transfer.num_vehicles || 1} = {formatCurrency(
                            (parseInt(opt.package_transfer.num_vehicles) || 1) * 
                            (parseFloat(opt.package_transfer.rate_per_vehicle) || parseFloat(opt.package_transfer.total_cost) || 0) *
                            (1 + (parseFloat(opt.package_transfer.gst_pct) || 0) / 100)
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* UX-01 — Misc Costs editor moved ABOVE P2P Transfers per
                  user request (Manual brief). The block itself was relocated
                  from below the Vehicle Upgrade table; ordering inside the
                  Manual Step-3 preview is now: Hotels → Vehicle Upgrade →
                  Misc Costs → P2P Transfers → Itinerary. */}
              <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3" data-testid="manual-misc-costs-editor">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Miscellaneous Costs
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addMiscCost}
                    className="h-8 text-xs"
                    data-testid="manual-add-misc-cost-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Cost
                  </Button>
                </div>
                {miscCosts.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No miscellaneous costs added</p>
                ) : (
                  <div className="space-y-2">
                    {miscCosts.map((cost) => (
                      <div key={cost.id} className={`p-3 rounded border ${cost.is_optional ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
                        <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                          <div className="w-full sm:w-24 lg:w-24 flex items-center">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!cost.is_optional}
                                onChange={(e) => updateMiscCost(cost.id, 'is_optional', !e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className={`text-xs font-medium ${!cost.is_optional ? 'text-green-700' : 'text-amber-700'}`}>
                                {!cost.is_optional ? 'Included' : 'Optional'}
                              </span>
                            </label>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Description</Label>
                            <Input
                              placeholder="e.g., Entry tickets, Guide charges"
                              value={cost.remarks}
                              onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="w-full sm:w-28 lg:w-28">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Type</Label>
                            <Select value={cost.cost_type} onValueChange={(v) => updateMiscCost(cost.id, 'cost_type', v)}>
                              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {COST_TYPES.map((ct) => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-full sm:w-24 lg:w-24">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Amount</Label>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={cost.amount}
                              onChange={(e) => updateMiscCost(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="w-full sm:w-20 lg:w-20">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">GST</Label>
                            <Select value={String(cost.gst_percentage || 0)} onValueChange={(v) => updateMiscCost(cost.id, 'gst_percentage', parseFloat(v))}>
                              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="GST" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">No GST</SelectItem>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="12">12%</SelectItem>
                                <SelectItem value="18">18%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMiscCost(cost.id)}
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {miscCosts.length > 0 && (
                      <div className="flex justify-between items-center pt-2 border-t border-navy/15">
                        <span className="text-sm font-medium text-navy">Total Miscellaneous</span>
                        <span className="text-sm font-bold text-navy">{formatCurrency(calculateMiscTotal(true))}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* P2P Transfers List - with fallback to formData when calculationResult not available */}
              {(() => {
                const p2pList = calculationResult?.p2p_transfers?.length > 0 
                  ? calculationResult.p2p_transfers 
                  : formData.manual_package_data?.p2p_transfers || [];
                
                if (p2pList.length === 0) return null;
                
                return (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2 text-sm">P2P Transfers</h4>
                    <div className="space-y-1 text-sm">
                      {p2pList.map((p2p, idx) => (
                        <div key={idx} className="flex justify-between text-orange-700">
                          <span>{p2p.from_place} → {p2p.to_place} ({p2p.transport_mode || 'Car'})</span>
                          <span>{formatCurrency(p2p.calculated_amount || p2p.total_cost || p2p.cost || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Batch-3 §7+§9: Vehicle Upgrade Options + Miscellaneous Costs
                  editor surfaced inside the Manual Step-3 preview. Previously
                  these workflow controls were only inside the Predefined /
                  Custom branches, so Manual agents had no way to add misc
                  charges or compare vehicle upgrades after Calculate.
                  BUG-08: relax the gate — when calculator hasn't emitted
                  `vehicle_upgrade_options`, fall back to the per-option
                  vehicles defined inside `manual_package_data.options[].package_transfer`
                  so Manual agents always see the upgrade picker. */}
              {(() => {
                let allVehiclesM = calculationResult?.vehicle_upgrade_options || [];
                if (allVehiclesM.length === 0) {
                  // Build from manual per-option vehicles
                  const opts = formData.manual_package_data?.options || [];
                  const seen = new Set();
                  allVehiclesM = opts.flatMap((o) => {
                    const pt = o.package_transfer || {};
                    const vt = pt.vehicle_name || pt.vehicle_type || '';
                    if (!vt || seen.has(vt)) return [];
                    seen.add(vt);
                    const rate = Number(pt.rate_per_vehicle || 0);
                    const count = Number(pt.num_vehicles || 1);
                    const total = Number(o.transfer_total || rate * count);
                    return [{
                      vehicle_type: vt,
                      vehicle_label: pt.vehicle_label || vt,
                      rate_per_day: rate,
                      vehicle_count: count,
                      total_quoted: total,
                      uplift_vs_base: 0,
                    }];
                  });
                }
                if (allVehiclesM.length === 0) return null;
                const baseVehicleType = formData.base_vehicle_type || calculationResult?.vehicle_type || allVehiclesM[0]?.vehicle_type;
                return (
                  <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3" data-testid="manual-vehicle-upgrade-table">
                    <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Vehicle Upgrade Options
                    </Label>
                    <p className="text-xs text-gray-500">Pick the base; check additional upgrades to expose them as options.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border border-navy/20">
                        <thead>
                          <tr className="bg-gray-50 border-b border-navy/20">
                            <th className="p-2 w-16 text-center text-navy font-semibold">Base</th>
                            <th className="p-2 w-10 text-center"></th>
                            <th className="p-2 text-left text-navy font-semibold">Vehicle</th>
                            <th className="p-2 text-center text-navy font-semibold">No. of Vehicles</th>
                            {!hideVehicleCosts && (
                              <>
                                <th className="p-2 text-center text-navy font-semibold">Days</th>
                                <th className="p-2 text-right text-navy font-semibold">Rate/Day</th>
                                <th className="p-2 text-right text-navy font-semibold">Total</th>
                              </>
                            )}
                            <th className="p-2 text-right text-navy font-semibold">Uplift</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allVehiclesM.map((v, idx) => {
                            const isBase = v.vehicle_type === baseVehicleType;
                            const isSel = isBase || selectedVehicles.includes(v.vehicle_type);
                            const days = calculationResult?.transfer_days || formData.transfer_days || 0;
                            const rate = v.rate_per_day || v.rate_amount || v.rate || 0;
                            const count = v.vehicle_count || v.count || 1;
                            // Bug 10: same fix as the primary preview vehicle table.
                            const total = rate * days * count;
                            const baseV = allVehiclesM.find((x) => x.vehicle_type === baseVehicleType) || allVehiclesM[0];
                            const uplift = vehicleUplift(v, baseV, rawVehicleTotal(days));
                            return (
                              <tr key={idx} className={`border-b border-navy/10 ${isBase ? 'bg-blue-50' : isSel ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                                <td className="p-2 text-center">
                                  <input
                                    type="radio"
                                    name="manual_base_vehicle"
                                    checked={isBase}
                                    onChange={() => {
                                      handleInputChange('base_vehicle_type', v.vehicle_type);
                                      handleInputChange('vehicle_type_selected', v.vehicle_type);
                                      if (!selectedVehicles.includes(v.vehicle_type) && onVehicleSelectionChange) {
                                        onVehicleSelectionChange(v.vehicle_type, true);
                                      }
                                      if (setHasChanges) setHasChanges(true);
                                    }}
                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <Checkbox
                                    checked={isSel}
                                    disabled={isBase}
                                    onCheckedChange={(checked) => {
                                      if (onVehicleSelectionChange) onVehicleSelectionChange(v.vehicle_type, checked);
                                      if (setHasChanges) setHasChanges(true);
                                    }}
                                    className="h-4 w-4"
                                  />
                                </td>
                                <td className="p-2 font-medium">
                                  {v.vehicle_label || v.label || v.vehicle_type}
                                  {isBase && <span className="ml-2 text-blue-600 text-[10px] font-semibold">(BASE)</span>}
                                </td>
                                <td className="p-2 text-center">{count}</td>
                                {!hideVehicleCosts && (
                                  <>
                                    <td className="p-2 text-center">{days}</td>
                                    <td className="p-2 text-right">{formatCurrency(rate)}/day</td>
                                    <td className="p-2 text-right font-medium">{formatCurrency(total)}</td>
                                  </>
                                )}
                                <td className="p-2 text-right">
                                  {isBase ? <span className="text-blue-600 font-medium">Included</span>
                                   : uplift > 0 ? <span className="text-orange-600">+ {formatCurrency(uplift)}</span>
                                   : uplift < 0 ? <span className="text-green-600">Save {formatCurrency(Math.abs(uplift))}</span>
                                   : <span className="text-gray-400">-</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* UX-01 — Original Miscellaneous Costs editor was here;
                  relocated above the P2P Transfers section so the Manual
                  Step-3 preview now reads: Hotels → Vehicle Upgrade → Misc
                  Costs → P2P Transfers → Itinerary. */}

              {/* Itinerary Preview */}
              {formData.manual_package_data?.itinerary?.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-3 text-sm">Day-wise Itinerary</h4>
                  <div className="space-y-2">
                    {formData.manual_package_data.itinerary.map((day, idx) => {
                      const itinerary = formData.manual_package_data.itinerary;
                      const isFirstDay = idx === 0;
                      const isLastDay = idx === itinerary.length - 1;
                      const arrivalCity = formData.arrival_city;
                      const departureCity = formData.departure_city;
                      
                      // Build enhanced description with arrival/departure context
                      let enhancedDescription = day.description || '';
                      if (isFirstDay && arrivalCity && !enhancedDescription.toLowerCase().includes(arrivalCity.toLowerCase())) {
                        enhancedDescription = `Arrive at ${arrivalCity}. ${enhancedDescription}`;
                      }
                      if (isLastDay && departureCity && !enhancedDescription.toLowerCase().includes(departureCity.toLowerCase())) {
                        enhancedDescription = `${enhancedDescription} Proceed to ${departureCity} for departure.`;
                      }
                      
                      return (
                        <div key={idx} className="text-sm border-l-2 border-purple-300 pl-3">
                          <div className="font-medium text-purple-900">Day {day.day}: {day.title}</div>
                          {enhancedDescription && (
                            <p className="text-purple-700 text-xs mt-1">{enhancedDescription}</p>
                          )}
                          {day.overnight_at && (
                            <p className="text-purple-600 text-xs italic">Overnight: {day.overnight_at}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Custom Package Preview - Full details like Predefined Route */}
        {calculationResult?.is_custom_package && (
          <Card data-testid="custom-package-preview">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-navy flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Custom Package Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Passenger & Room Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-white rounded-lg border border-navy/15">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Passengers</p>
                    <p className="text-xs text-gray-600">
                      {formData.no_adults} Adult{formData.no_adults !== 1 ? 's' : ''}
                      {formData.no_children_with_bed > 0 && ` • ${formData.no_children_with_bed}C(bed)`}
                      {formData.no_children_no_bed > 0 && ` • ${formData.no_children_no_bed}C(no bed)`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Bed className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Rooms</p>
                    <p className="text-xs text-gray-600">
                      {calculationResult.single_rooms > 0 && `${calculationResult.single_rooms}S `}
                      {calculationResult.double_rooms > 0 && `${calculationResult.double_rooms}D `}
                      {calculationResult.triple_rooms > 0 && `${calculationResult.triple_rooms}T`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Car className="w-4 h-4 text-navy mt-0.5" />
                  <div>
                    <p className="font-semibold text-navy text-sm">Vehicle</p>
                    <p className="text-xs text-gray-600" data-testid="custom-vehicle-summary">
                      {(calculationResult.vehicle_info?.vehicle_count || 1) > 1 ? `${calculationResult.vehicle_info.vehicle_count} × ` : ''}
                      {calculationResult.vehicle_type || formData.vehicle_type || 'Auto'}
                    </p>
                    <p className="text-xs text-gray-500">{calculationResult.transfer_days || formData.transfer_days || 0} days</p>
                  </div>
                </div>
              </div>

              {/* Hotels Selected — one block per option (each option is its
                  own price tier). Rows are flattened from the per-stop
                  breakdown, which nests `hotels` (split stays included). */}
              {(() => {
                const flatten = (breakdown) =>
                  (breakdown || []).flatMap((stop) =>
                    (stop.hotels || []).map((h) => ({
                      ...h,
                      destination: h.destination || stop.place_name,
                      nights: h.nights || stop.nights,
                    }))
                  );
                const blocks = (calculationResult.options || []).length > 0
                  ? calculationResult.options.map((o) => ({
                      name: o.option_name,
                      hotels: flatten(o.hotel_breakdown),
                    }))
                  : [{
                      name: null,
                      hotels: (calculationResult.hotels_selected || []).length > 0
                        ? calculationResult.hotels_selected
                        : flatten(calculationResult.hotel_breakdown),
                    }];
                const filled = blocks.filter((b) => b.hotels.length > 0);
                if (filled.length === 0) return null;

                return (
                  <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-4" data-testid="custom-hotels-selected">
                    <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                      <Hotel className="w-4 h-4" />
                      Hotels Selected
                    </Label>
                    {filled.map((block, bIdx) => (
                      <div key={bIdx} className="space-y-1" data-testid={`custom-hotels-option-${bIdx}`}>
                        {block.name && (
                          <p className="text-xs font-semibold text-navy">{block.name}</p>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-navy/25">
                            <thead>
                              <tr className="bg-navy text-white">
                                <th className="border border-navy/50 p-2 text-left">Destination</th>
                                <th className="border border-navy/50 p-2 text-left">Hotel</th>
                                <th className="border border-navy/50 p-2 text-left">Room Name</th>
                                <th className="border border-navy/50 p-2 text-center">Rooms</th>
                                <th className="border border-navy/50 p-2 text-center">Nights</th>
                              </tr>
                            </thead>
                            <tbody>
                              {block.hotels.map((hotel, idx) => (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="border border-navy/25 p-2 font-medium">{hotel.destination || hotel.place_name || hotel.stop_name}</td>
                                  <td className="border border-navy/25 p-2">{hotel.hotel_name}</td>
                                  <td className="border border-navy/25 p-2 text-green-600">{hotel.room_name || '—'}</td>
                                  <td className="border border-navy/25 p-2 text-center">{hotelRoomsText(hotel.num_rooms || hotel.rooms_required)}</td>
                                  <td className="border border-navy/25 p-2 text-center">{hotel.nights}N</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Vehicle Options */}
              {(() => {
                const allVehicles = calculationResult?.vehicle_upgrade_options?.length
                  ? calculationResult.vehicle_upgrade_options
                  : (vehicleOptions || []);
                if (allVehicles.length === 0) return null;
                
                return (
                  <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                    <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Vehicle Options
                    </Label>
                    {/* Transfer days — shared by every option. Until 2026-09-27
                        this was asked once per option back in Step 2. */}
                    <div className="flex flex-wrap items-center gap-3 p-2 bg-gray-50 rounded border border-navy/10">
                      <Label className="text-xs font-medium text-navy">Transfer Days</Label>
                      <Input
                        type="number"
                        min="0"
                        className="h-8 w-24"
                        value={formData.transfer_days_auto === false
                          ? (formData.transfer_days ?? customAutoTransferDays)
                          : customAutoTransferDays}
                        disabled={formData.transfer_days_auto !== false}
                        onChange={(e) => {
                          handleInputChange('transfer_days', parseInt(e.target.value) || 0);
                          if (setHasChanges) setHasChanges(true);
                        }}
                        data-testid="custom-transfer-days-input"
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5"
                          checked={formData.transfer_days_auto !== false}
                          onChange={(e) => {
                            handleInputChange('transfer_days_auto', e.target.checked);
                            handleInputChange(
                              'transfer_days',
                              e.target.checked
                                ? customAutoTransferDays
                                : (formData.transfer_days || customAutoTransferDays)
                            );
                            if (setHasChanges) setHasChanges(true);
                          }}
                          data-testid="custom-transfer-days-auto"
                        />
                        Auto ({customAutoTransferDays} = nights + 1)
                      </label>
                    </div>
                    <CustomVehicleTable
                      vehicles={allVehicles}
                      baseVehicleType={formData.base_vehicle_type || calculationResult?.vehicle_type || allVehicles[0]?.vehicle_type}
                      counts={formData.vehicle_counts || {}}
                      selectedVehicles={selectedVehicles}
                      seatsNeeded={(formData.no_adults || 0) + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0)}
                      routeName={calculationResult?.vehicle_info?.route_name}
                      formatCurrency={formatCurrency}
                      onBaseChange={(vt) => {
                        handleInputChange('base_vehicle_type', vt);
                        handleInputChange('vehicle_type_selected', vt);
                        if (onVehicleSelectionChange && selectedVehicles.includes(vt)) onVehicleSelectionChange(vt, false);
                        if (setHasChanges) setHasChanges(true);
                      }}
                      onTick={(vt, checked) => onVehicleSelectionChange && onVehicleSelectionChange(vt, checked)}
                      onCountChange={(vt, n) => {
                        handleInputChange('vehicle_counts', { ...(formData.vehicle_counts || {}), [vt]: n });
                        if (setHasChanges) setHasChanges(true);
                      }}
                    />
                  </div>
                );
              })()}

              {/* Point-to-Point Transfers — SHARED across every option.
                  Moved here 2026-09-27: the Step-2 option cards collected them
                  but they never reached pricing. */}
              <div className={`p-4 bg-white rounded-lg border border-navy/15 space-y-3${P2P_DISABLED ? " hidden" : ""}`} data-testid="custom-p2p-section">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Point-to-Point Transfers
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addManualP2P}
                    className="h-8 text-xs"
                    data-testid="add-p2p-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Transfer
                  </Button>
                </div>
                {(formData.p2p_transfers || []).length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No point-to-point transfers added</p>
                ) : (
                  <div className="space-y-2">
                    {(formData.p2p_transfers || []).map((p2p, idx) => (
                      <div
                        key={p2p.id || idx}
                        className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded border border-navy/10"
                        data-testid={`p2p-row-${idx}`}
                      >
                        <Input
                          placeholder="From"
                          value={p2p.from_place || ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'from_place', e.target.value)}
                          className="h-8 w-28 text-xs"
                          data-testid={`p2p-from-${idx}`}
                        />
                        <span className="text-gray-400">→</span>
                        <Input
                          placeholder="To"
                          value={p2p.to_place || ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'to_place', e.target.value)}
                          className="h-8 w-28 text-xs"
                          data-testid={`p2p-to-${idx}`}
                        />
                        <Input
                          placeholder="Mode"
                          value={p2p.transport_mode || ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'transport_mode', e.target.value)}
                          className="h-8 w-24 text-xs"
                          data-testid={`p2p-mode-${idx}`}
                        />
                        <Input
                          type="number"
                          placeholder="Rate"
                          value={p2p.rate || ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'rate', parseFloat(e.target.value) || 0)}
                          className="h-8 w-24 text-xs"
                          data-testid={`p2p-rate-${idx}`}
                        />
                        <span className="text-xs text-gray-500">×</span>
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={p2p.quantity || ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'quantity', parseInt(e.target.value) || 1)}
                          className="h-8 w-16 text-xs"
                          data-testid={`p2p-qty-${idx}`}
                        />
                        <Input
                          type="number"
                          placeholder="GST %"
                          value={p2p.gst_slab ?? ''}
                          onChange={(e) => updateManualP2P(p2p.id, 'gst_slab', parseFloat(e.target.value) || 0)}
                          className="h-8 w-20 text-xs"
                          data-testid={`p2p-gst-${idx}`}
                        />
                        <span className="text-xs font-medium text-navy ml-auto" data-testid={`p2p-amount-${idx}`}>
                          {formatCurrency(
                            (parseFloat(p2p.rate) || 0) * (parseInt(p2p.quantity) || 1) *
                              (1 + (parseFloat(p2p.gst_slab) || 0) / 100)
                          )}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeManualP2P(p2p.id)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          data-testid={`p2p-remove-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Miscellaneous Costs - Editable */}
              <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Miscellaneous Costs
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addMiscCost}
                    className="h-8 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Cost
                  </Button>
                </div>
                
                {miscCosts.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No miscellaneous costs added</p>
                ) : (
                  <div className="space-y-2">
                    {miscCosts.map((cost, idx) => (
                      <div key={cost.id} className={`p-3 rounded border ${cost.is_optional ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`} data-testid={`custom-misc-row-${idx}`}>
                        {/* #289720: Custom Misc Costs now edited here (step 3) with the Included/Optional toggle */}
                        <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                          <div className="w-full sm:w-24 lg:w-24 flex items-center">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!cost.is_optional}
                                onChange={(e) => updateMiscCost(cost.id, 'is_optional', !e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                data-testid={`custom-misc-optional-${idx}`}
                              />
                              <span className={`text-xs font-medium ${!cost.is_optional ? 'text-green-700' : 'text-amber-700'}`}>
                                {!cost.is_optional ? 'Included' : 'Optional'}
                              </span>
                            </label>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Description</Label>
                            <Input
                              placeholder="e.g., Entry tickets, Guide charges"
                              value={cost.remarks}
                              onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="w-full sm:w-28 lg:w-28">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Type</Label>
                            <Select value={cost.cost_type} onValueChange={(v) => updateMiscCost(cost.id, 'cost_type', v)}>
                              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {COST_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-full sm:w-24 lg:w-24">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Amount</Label>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={cost.amount}
                              onChange={(e) => updateMiscCost(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="w-full sm:w-20 lg:w-20">
                            <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">GST</Label>
                            <Select value={String(cost.gst_percentage || 0)} onValueChange={(v) => updateMiscCost(cost.id, 'gst_percentage', parseFloat(v))}>
                              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="GST" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">No GST</SelectItem>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="12">12%</SelectItem>
                                <SelectItem value="18">18%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMiscCost(cost.id)}
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Option-wise pricing — one tier per option, side by side. */}
              {(() => {
                const tiers = Object.entries(calculationResult.category_pricing || {});
                if (tiers.length === 0) return null;
                const showDiscount = !!formData.show_discount_separately;
                const gstIncluded = !!formData.gst_included;
                const gstPct = Number(calculationResult.gst_percentage || formData.gst_percentage || 0);
                // Server figure keeps the summary identical to the saved quote.
                const includedMisc = calculationResult.misc_included_total ?? calculateMiscTotal(false);
                const hasP2p = tiers.some(([, t]) => Number(t.p2p_cost || 0) > 0);
                // Ticket #285275 (#8): Hotels/Transfers/P2P/Misc split only when the
                // break-up toggle is ON (B2B always sees it). Values include markup.
                const showSplit = isB2BAgent || formData.show_breakup === true;
                const splitOf = (t) => {
                  const h = Number(t.hotel_cost || 0), tr = Number(t.transfer_cost || 0), p = Number(t.p2p_cost || 0);
                  const base = h + tr + p, mk = Number(t.markup_amount || 0);
                  const add = (v) => (base > 0 ? v + mk * (v / base) : v);
                  return { hotel: add(h), transfer: add(tr), p2p: add(p) };
                };

                return (
                  <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-2" data-testid="custom-options-pricing">
                    <Label className="text-sm font-semibold text-navy flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Option-wise Pricing
                    </Label>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border border-navy/25">
                        <thead>
                          <tr className="bg-navy text-white">
                            <th className="border border-navy/50 p-2 text-left">Option</th>
                            {showSplit && <th className="border border-navy/50 p-2 text-right">Hotels</th>}
                            {showSplit && <th className="border border-navy/50 p-2 text-right">Transfers</th>}
                            {showSplit && hasP2p && <th className="border border-navy/50 p-2 text-right">P2P</th>}
                            <th className="border border-navy/50 p-2 text-right">Base</th>
                            {showDiscount && <th className="border border-navy/50 p-2 text-right">Discount</th>}
                            {!gstIncluded && <th className="border border-navy/50 p-2 text-right">GST</th>}
                            <th className="border border-navy/50 p-2 text-right">Grand Total</th>
                            <th className="border border-navy/50 p-2 text-right">Per Person</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tiers.map(([name, tier], idx) => {
                            const net = Number(tier.net_quoted_price || 0);
                            const discount = Number(tier.discount_amount || 0);
                            const base = showDiscount ? net + discount : net;
                            return (
                              <tr key={name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`option-price-row-${idx}`}>
                                <td className="border border-navy/25 p-2 font-medium text-navy" data-testid={`option-price-name-${idx}`}>{name}</td>
                                {showSplit && <td className="border border-navy/25 p-2 text-right">{formatCurrency(splitOf(tier).hotel)}</td>}
                                {showSplit && <td className="border border-navy/25 p-2 text-right">{formatCurrency(splitOf(tier).transfer)}</td>}
                                {showSplit && hasP2p && <td className="border border-navy/25 p-2 text-right">{formatCurrency(splitOf(tier).p2p)}</td>}
                                <td className="border border-navy/25 p-2 text-right">{formatCurrency(base)}</td>
                                {showDiscount && (
                                  <td className="border border-navy/25 p-2 text-right text-red-600">-{formatCurrency(discount)}</td>
                                )}
                                {!gstIncluded && (
                                  <td className="border border-navy/25 p-2 text-right">{formatCurrency(tier.gst_amount || 0)}</td>
                                )}
                                <td className="border border-navy/25 p-2 text-right font-semibold" data-testid={`option-grand-total-${idx}`}>
                                  {formatCurrency(tier.total_with_gst || 0)}
                                </td>
                                <td className="border border-navy/25 p-2 text-right">{formatCurrency(tier.per_person_price || 0)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {includedMisc > 0 && (
                      <MiscSummaryTable
                        testId="misc-summary-custom"
                        rows={tiers.map(([name, t]) => ({
                          option: name, package: formatCurrency(t.total_with_gst || 0), misc: formatCurrency(includedMisc),
                          grand: formatCurrency(Number(t.total_with_gst || 0) + includedMisc),
                        }))}
                      />
                    )}
                    {gstIncluded && gstPct > 0 && (
                      <p className="text-xs text-gray-500" data-testid="custom-options-gst-note">
                        {tiers.some(([, t]) => Number(t.gst_amount || 0) > 0)
                          ? `GST @ ${gstPct}% included in Total Package cost`
                          : `GST @ ${gstPct}% included in every option price`}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Pricing Summary */}
              {/* Bug 8 (2026-02): Package Total must equal what the customer
                  pays. When gst_included=false, add GST to the net. When true,
                  net already contains GST and we emit a footnote instead. */}
              {Object.keys(calculationResult.category_pricing || {}).length <= 1 && (() => {
                const netQuoted = Number(calculationResult.net_quoted_price || calculationResult.total_cost || 0);
                const gstAmt = Number(calculationResult.gst_amount || 0);
                const gstPct = Number(calculationResult.gst_percentage || formData.gst_percentage || 0);
                const gstIncl = !!formData.gst_included;
                const showGstLine = !gstIncl && gstAmt > 0;
                // Ticket #285275 (Option A): included + gst_amount > 0 means GST was
                // added to the price - total = net + GST, line stays hidden.
                const gstInTotal = gstIncl && gstAmt > 0;
                const packageTotal = (gstIncl && !gstInTotal) ? netQuoted : (netQuoted + gstAmt);
                const showDiscLine = !!formData.show_discount_separately && (calculationResult.discount_amount || 0) > 0;
                // Ticket #285275 (#7): no separate Markup line - markup is spread
                // into Accommodation / Transfer (same rule as the PDF) so the lines
                // still add up to the Package Total.
                const { displayHotel, displayTransfer } = applyMarkupDistribution({
                  hotel_cost: calculationResult.hotel_cost || calculationResult.accommodation_cost || 0,
                  transfer_cost: calculationResult.transfer_cost || 0,
                  p2p_cost: calculationResult.p2p_cost || 0,
                  markup_amount: calculationResult.markup_amount || 0,
                });
                return (
              <div className="bg-navy text-white rounded-xl p-4 shadow-lg" data-testid="grand-total-summary">
                <div className="space-y-2">
                  {(isB2BAgent || formData.show_breakup === true) && (calculationResult.hotel_cost || calculationResult.accommodation_cost || 0) > 0 && (
                    <div className="flex justify-between text-white/70">
                      <span>Accommodation</span>
                      <span>{formatCurrency(displayHotel)}</span>
                    </div>
                  )}
                  {(isB2BAgent || formData.show_breakup === true) && (calculationResult.transfer_cost || 0) > 0 && (
                    <div className="flex justify-between text-white/70">
                      <span>Transfer ({calculationResult.total_days || calculationResult.transfer_days || 0} days)</span>
                      <span>{formatCurrency(displayTransfer)}</span>
                    </div>
                  )}
                  {showDiscLine && (
                    <div className="flex justify-between text-red-300" data-testid="summary-discount-line">
                      <span>- Discount</span>
                      <span>-{formatCurrency(calculationResult.discount_amount || 0)}</span>
                    </div>
                  )}
                  {showGstLine && (
                    <div className="flex justify-between text-white/70" data-testid="summary-gst-line">
                      <span>+ GST ({gstPct}%)</span>
                      <span>+ {formatCurrency(gstAmt)}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-white/30" data-testid="summary-package-total">
                  <span className="font-semibold">Package Total</span>
                  <span className="text-2xl font-bold">{formatCurrency(packageTotal)}</span>
                </div>
                <MiscLines
                  packageTotal={packageTotal}
                  misc={calculationResult.misc_included_total ?? calculateMiscTotal(false)}
                  formatCurrency={formatCurrency}
                />
                {gstIncl && gstPct > 0 && (
                  <div className="text-xs text-white/60 text-right mt-1" data-testid="summary-gst-included-note">
                    {gstInTotal
                      ? `GST @ ${gstPct}% included in Total Package cost`
                      : `GST @ ${gstPct}% included in package price`}
                  </div>
                )}
                <div className="flex justify-between text-white/70 mt-1">
                  <span>Per Person ({(formData.no_adults || 0) + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0)} pax)</span>
                  <span>{formatCurrency(((formData.no_adults || 0) + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0)) > 0 ? packageTotal / ((formData.no_adults || 0) + (formData.no_children_with_bed || 0) + (formData.no_children_no_bed || 0)) : 0)}</span>
                </div>
              </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Payment Policy */}
        {paymentPolicies.length > 0 && (
          <Card data-testid="payment-policy-card">
            <CardHeader className="pb-2 bg-navy/5">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-navy" />
                <CardTitle className="text-base font-semibold text-navy">Payment Policy</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="w-28">Select Policy:</Label>
                  <Select
                    value={selectedPolicy || ''}
                    onValueChange={(v) => {
                      setSelectedPolicy(v);
                      handleInputChange('payment_policy_id', v);
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a payment policy" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentPolicies.map(policy => (
                        <SelectItem key={policy.policy_id} value={policy.policy_id}>
                          {policy.policy_name} {policy.is_default && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Terms Section */}
        <TermsSection formData={formData} handleInputChange={handleInputChange} />

        {/* UX-03 — Recalculate Button always at bottom (above Save).
            Previously only rendered when !hasChanges, forcing the user to
            scroll up to the warning banner when changes existed. Now
            available consistently as the bottom anchor of the preview. */}
        <Button
          onClick={onCalculate}
          variant="outline"
          className="w-full border-navy text-navy hover:bg-navy/5"
          disabled={calculating}
          data-testid="recalculate-btn"
        >
          {calculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
          Recalculate Quote
        </Button>

        {/* Save Quotation Button */}
        <Button
          onClick={onSave}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base"
          disabled={loading || hasChanges}
          data-testid="save-quotation-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving Quotation...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5 mr-2" />
              Save Quotation
            </>
          )}
        </Button>
        
        {hasChanges && (
          <div className="space-y-2">
            <Button
              onClick={onRecalculateAndSave}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-base"
              disabled={loading || calculating}
              data-testid="recalculate-and-save-btn"
            >
              {calculating || loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Calculator className="w-5 h-5 mr-2" />
              )}
              Recalculate &amp; Save
            </Button>
            <p className="text-center text-sm text-amber-600" data-testid="stale-save-note">
              Save is locked until prices are refreshed
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Trip Summary Accordion - At top for Predefined/Custom Route, collapsed by default */}
      <TripSummaryAccordion
        formData={formData}
        calculationResult={calculationResult}
        defaultOpen={false}
      />

      <Card data-testid="step-3-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg sm:text-xl">Preferences & Generate Quote</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Category Classification Mode & Selection - Hide for DMC and Manual packages */}
          {!isDMCPackage && !isManualPackage && (
          <>
          <div className="space-y-3">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-navy">Hotel Categories</Label>
              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => switchClassificationMode && switchClassificationMode('PRICE_RANGE')}
                  className={`px-3 py-1 rounded-l-lg border transition-all ${
                    (classificationMode || formData.classification_mode) === 'PRICE_RANGE'
                      ? 'bg-navy text-white border-navy'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="mode-price-range"
                >
                  Price Range
                </button>
                <button
                  type="button"
                  onClick={() => switchClassificationMode && switchClassificationMode('STAR')}
                  className={`px-3 py-1 rounded-r-lg border transition-all ${
                    (classificationMode || formData.classification_mode) === 'STAR'
                      ? 'bg-navy text-white border-navy'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="mode-star"
                >
                  Star Rating
                </button>
              </div>
            </div>
            
            {/* Category Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(availableCategories.length > 0 ? availableCategories : ROOM_CATEGORIES).map((category) => (
                <div
                  key={category}
                  className={`flex items-center space-x-2 p-2 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.selected_categories.includes(category)
                      ? 'border-navy bg-white'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleCategory(category)}
                  data-testid={`category-${category.toLowerCase().replace(/[★\s]/g, '')}`}
                >
                  <Checkbox
                    checked={formData.selected_categories.includes(category)}
                    onCheckedChange={() => toggleCategory(category)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs font-medium truncate">{category.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Meal Plan Selection - Hide for DMC packages */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-navy">Meal Plan</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {availableMealPlans.map((plan) => (
                <div
                  key={plan.value}
                  className={`flex items-center space-x-2 p-2 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.meal_plans.includes(plan.value)
                      ? 'border-navy bg-white'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleMealPlan(plan.value)}
                  data-testid={`meal-plan-${plan.value}`}
                >
                  <Checkbox
                    checked={formData.meal_plans.includes(plan.value)}
                    onCheckedChange={() => toggleMealPlan(plan.value)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs font-medium">{plan.label}</span>
                </div>
              ))}
            </div>
          </div>
          </>
          )}

          {/* Markup Section — hidden for B2B agents on Predefined/Custom;
              Admin's default margin applies automatically server-side. */}
          {!hideMarkupForB2B && (
          <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
            <Label className="text-sm font-semibold text-navy flex items-center gap-2">
              <Calculator className="w-4 h-4 text-navy" />
              Markup & Discount
            </Label>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Markup Type Toggle */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Markup Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.markup_type === 'percent' ? 'default' : 'outline'}
                    onClick={() => handleInputChange('markup_type', 'percent')}
                    className="flex-1 h-9 text-xs"
                  >
                    Percentage (%)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.markup_type === 'amount' ? 'default' : 'outline'}
                    onClick={() => handleInputChange('markup_type', 'amount')}
                    className="flex-1 h-9 text-xs"
                  >
                    Fixed Amount (₹)
                  </Button>
                </div>
              </div>

              {/* Markup Value */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">
                  {formData.markup_type === 'percent' ? 'Markup %' : 'Markup Amount'}
                </Label>
                <div className="flex gap-2 items-center">
                  <ClearableNumberInput
                    min={0}
                    max={formData.markup_type === 'percent' ? 100 : 9999999}
                    value={formData.markup_type === 'percent' ? formData.markup_pct : formData.markup_amount}
                    onChange={(val) => {
                      if (formData.markup_type === 'percent') {
                        handleInputChange('markup_pct', val);
                      } else {
                        handleInputChange('markup_amount', val);
                      }
                      // Auto-recalc removed 2026-07-29 per operator feedback.
                      // The user must press "Calculate Quote" to fire /calculate.
                      // This eliminates the burst-of-calls that was causing
                      // Cloudflare 502s on 2-category Predefined saves.
                    }}
                    className="h-9"
                    data-testid="markup-input"
                  />
                  {formData.markup_type === 'amount' && (
                    <div className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <Checkbox
                        id="markup-per-person"
                        checked={formData.markup_per_person}
                        onCheckedChange={(checked) => handleInputChange('markup_per_person', checked)}
                      />
                      <Label htmlFor="markup-per-person" className="text-xs cursor-pointer">Per Person</Label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Discount Section - Similar to Markup */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Discount Type Toggle */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Discount Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={(formData.discount_type || 'percent') === 'percent' ? 'default' : 'outline'}
                    onClick={() => handleInputChange('discount_type', 'percent')}
                    className="flex-1 h-9 text-xs"
                  >
                    Percentage (%)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.discount_type === 'amount' ? 'default' : 'outline'}
                    onClick={() => handleInputChange('discount_type', 'amount')}
                    className="flex-1 h-9 text-xs"
                  >
                    Fixed Amount (₹)
                  </Button>
                </div>
              </div>

              {/* Discount Value */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">
                  {(formData.discount_type || 'percent') === 'percent' ? 'Discount %' : 'Discount Amount'}
                </Label>
                <div className="flex gap-2 items-center">
                  <ClearableNumberInput
                    min={0}
                    max={(formData.discount_type || 'percent') === 'percent' ? 100 : 9999999}
                    value={(formData.discount_type || 'percent') === 'percent' ? formData.discount_pct : (formData.discount_amount || 0)}
                    onChange={(val) => {
                      if ((formData.discount_type || 'percent') === 'percent') {
                        handleInputChange('discount_pct', val);
                      } else {
                        handleInputChange('discount_amount', val);
                      }
                      // Auto-recalc removed 2026-07-29 (see markup-input).
                    }}
                    className="h-9"
                    data-testid="discount-input"
                  />
                  {formData.discount_type === 'amount' && (
                    <div className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <Checkbox
                        id="discount-per-person"
                        checked={formData.discount_per_person}
                        onCheckedChange={(checked) => handleInputChange('discount_per_person', checked)}
                      />
                      <Label htmlFor="discount-per-person" className="text-xs cursor-pointer">Per Person</Label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Show Breakup Toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <Label className="text-xs text-gray-600">Show Cost Breakup in Quote</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.show_breakup}
                  onCheckedChange={(checked) => handleInputChange('show_breakup', checked)}
                />
                <span className="text-xs">{formData.show_breakup ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {/* Show Discount Separately */}
            {(formData.discount_pct > 0 || formData.discount_amount > 0) && (
              <div className="flex items-center justify-between pt-2 border-t">
                <Label className="text-xs text-gray-600">Show Discount Separately</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.show_discount_separately}
                    onCheckedChange={(checked) => handleInputChange('show_discount_separately', checked)}
                  />
                  <span className="text-xs">{formData.show_discount_separately ? 'Yes (as line item)' : 'No (included)'}</span>
                </div>
              </div>
            )}
          </div>
          )}

          {/* GST Section */}
          <div className="p-4 bg-white rounded-lg border border-navy/15 space-y-3">
            <Label className="text-sm font-semibold text-navy flex items-center gap-2">
              <Receipt className="w-4 h-4 text-navy" />
              GST Configuration
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">GST Rate</Label>
                <Select
                  value={String(formData.gst_percentage)}
                  onValueChange={(v) => { handleInputChange('gst_percentage', parseFloat(v)); }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No GST (0%)</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Apply GST On</Label>
                <Select
                  value={formData.gst_apply_on || 'total'}
                  onValueChange={(v) => handleInputChange('gst_apply_on', v)}
                  disabled={formData.gst_percentage === 0}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total">Total Package Amount</SelectItem>
                    <SelectItem value="markup">Markup Amount Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="gst-included"
                  checked={formData.gst_included}
                  onCheckedChange={(checked) => handleInputChange('gst_included', checked)}
                  disabled={formData.gst_percentage === 0}
                />
                <Label htmlFor="gst-included" className="text-xs cursor-pointer">
                  {formData.gst_included ? 'GST Included in Quote' : 'Show GST Separately'}
                </Label>
              </div>
            </div>
            {formData.gst_percentage > 0 && (
              <p className="text-xs text-gray-600">
                {formData.gst_apply_on === 'markup' 
                  ? `GST @ ${formData.gst_percentage}% will be applied on markup amount only`
                  : `GST @ ${formData.gst_percentage}% will be applied on total package amount`}
                {formData.gst_included ? ' (included in price)' : ' (shown separately)'}
              </p>
            )}
          </div>

          {/* Quotation Format Selection */}
          <div className="space-y-3 p-4 bg-white border border-navy/15 rounded-lg">
            <Label className="text-sm font-semibold text-navy flex items-center gap-2">
              <FileText className="w-4 h-4 text-navy" />
              Quotation Format
            </Label>
            <RadioGroup 
              value={formData.quotation_format} 
              onValueChange={(v) => handleInputChange('quotation_format', v)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <div className={`flex items-start space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.quotation_format === 'total' ? 'border-navy bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <RadioGroupItem value="total" id="format-total" className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="format-total" className="cursor-pointer font-medium text-sm">Total Package</Label>
                  <p className="text-xs text-gray-600 mt-0.5">Single total price for the entire group</p>
                </div>
              </div>
              <div className={`flex items-start space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.quotation_format === 'person_wise' ? 'border-navy bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <RadioGroupItem value="person_wise" id="format-person" className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="format-person" className="cursor-pointer font-medium text-sm">Person-wise</Label>
                  <p className="text-xs text-gray-600 mt-0.5">Adults pay hotel + transport, Children pay bed only</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Calculate/Update Button - Navy with White text */}
          <Button
            onClick={onCalculate}
            className="w-full bg-navy hover:bg-navy-mid text-white h-11"
            disabled={calculating}
            data-testid="calculate-button"
          >
            {calculating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isManualPackage ? 'Updating...' : 'Calculating...'}
              </>
            ) : (
              <>
                <Calculator className="w-4 h-4 mr-2" />
                {isManualPackage ? 'Update Quote' : 'Calculate Quote'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

    </div>
  );
};

export default PreferencesStep;
